import { openGitHubInstallPopup } from '@/pages/integrations/providers/github/GitHubProvider'
import {
  useState,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useMemo,
  type ReactNode,
} from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useSetAtom } from 'jotai'
import {
  Loader2,
  CheckCircle2,
  CircleAlert,
  ChevronDown,
  ChevronRight,
  LayoutGrid,
  CalendarClock,
  FileText,
  Database,
  Shield,
  Wrench,
  Search,
  Cloud,
  Code2,
  Eye,
  BookOpen,
  Ticket,
  ClipboardCheck,
  AlertTriangle,
  Github,
  ArrowRight,
  Mail,
  Send,
  SkipForward,
  ListTodo,
  Users,
  UserCheck,
  Clock,
  ExternalLink,
} from 'lucide-react'
import { toast } from 'sonner'
import {
  openMainPanelOverlayAtom,
  closeMainPanelOverlayAtom,
} from '@/stores/mainPanelOverlayStore'
import {
  artifactPanelStateAtom,
  chatProgrammaticMessageAtom,
  type ArtifactPanelState,
} from '@/stores/screenataPageContextStore'
import { trpc } from '@/lib/trpc'
import { useEnabledFrameworks } from '@/hooks/useComplianceFramework'
import { formatEnrolledFrameworksLabel } from '@/lib/compliance-framework'
import { Button } from '@deepguide-ai/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@deepguide-ai/ui/dialog'
import { useChatStopped } from '@/components/chat/chat-stopped-context'
import {
  Marker,
  MarkerContent,
  MarkerIcon,
} from '@/components/ai-elements/marker'
import { PoliciesGenerateToolMessage } from '@/components/chat/components/messages/tools/perAction/PoliciesToolMessages'
import { RunbookRunContent } from '@/components/compliance/RunbookRunDetail'
import { AnalysisDrawer } from '@/components/screenata/AnalysisDrawer'
import { ConnectionResult } from '@/components/screenata/scan/ConnectionResult'
import { useScreenataCloudConnectionManager } from '@/components/screenata/cloud-connections'
import { ToolErrorGuidance } from '../ToolErrorGuidance'
import { PresentOptionsMessage } from './PresentOptionsMessage'

interface ToolPartProps {
  part: any
  icon?: React.ReactNode
  shouldPoll?: boolean
  /**
   * True when this tool's parent message is the last assistant
   * message in the conversation. Used by tool components that only
   * want to auto-open UI on a fresh send (and stay quiet for older
   * messages on page reload). Optional — tools that don't need it
   * just ignore the flag.
   */
  isLastAssistantMessage?: boolean
}

const autoDispatchedScanRunIds = new Set<string>()
const SCAN_CHECK_LABELS: Record<string, string> = {
  'code.secrets': 'Hardcoded secrets',
  'code.gitignore_env': '.env protection',
  'code.sensitive_logging': 'Sensitive logging',
  'code.access_controls': 'Access control',
  'code.change_management': 'Change management',
  'code.vulnerability_monitoring': 'Vulnerability monitoring',
  'code.network_security': 'Network security',
  'code.data_management': 'Data management',
}

// ── Shared helpers (same as PolicyToolMessages) ──────────────────────────────

/** Dispatch a user-selected option back to the active chat agent */
function dispatchChatOption(text: string) {
  const isOnboarding = window.location.pathname.includes('/onboarding')
  const scope = isOnboarding ? 'screenata-onboarding-chat' : 'screenata-chat'
  window.dispatchEvent(
    new CustomEvent('screenata:option-selected', {
      detail: { text, scope },
    })
  )
}

function getToolState(part: any): {
  isComplete: boolean
  isError: boolean
  result: any
  args: any
  awaitingApproval: boolean
} {
  if (part.type === 'tool-input-error') {
    return {
      isComplete: true,
      isError: true,
      result: null,
      args: part.input ?? {},
      awaitingApproval: false,
    }
  }
  if (part.state) {
    const isComplete =
      part.state === 'output-available' || part.state === 'error'
    const isError = part.state === 'error'
    let result = null
    if (isComplete && part.output != null) {
      try {
        result =
          typeof part.output === 'string'
            ? JSON.parse(part.output)
            : part.output
      } catch {
        /* ignore */
      }
    }
    // `needsApproval` tools pause here until the user clicks the
    // in-chat approval card. The AI SDK reports the pause as
    // `state === 'approval-requested'` or by attaching `part.approval`
    // — the latter persists after stream stop. Surfacing this lets
    // ToolStatusLine suppress the misleading "Failed:" label.
    const awaitingApproval =
      !isComplete &&
      (part.state === 'approval-requested' || part.approval != null)
    return {
      isComplete,
      isError,
      result,
      args: part.input ?? {},
      awaitingApproval,
    }
  }
  const ti = part.toolInvocation
  if (ti) {
    const isComplete = ti.state === 'result' || ti.state === 'error'
    const isError = ti.state === 'error'
    let result = null
    if (isComplete && ti.result) {
      try {
        result =
          typeof ti.result === 'string' ? JSON.parse(ti.result) : ti.result
      } catch {
        /* ignore */
      }
    }
    return {
      isComplete,
      isError,
      result,
      args: ti.args ?? {},
      awaitingApproval: false,
    }
  }
  return {
    isComplete: false,
    isError: false,
    result: null,
    args: {},
    awaitingApproval: false,
  }
}

function isToolError(isComplete: boolean, result: any): boolean {
  if (!isComplete || !result) return false
  return result.ok === false || result.success === false || !!result.error
}

function getErrorMessage(result: any): string | null {
  if (!result) return null
  return result.error ?? result.message ?? result.errorCode ?? null
}

function getScanCheckLabel(checkId: string): string {
  return SCAN_CHECK_LABELS[checkId] ?? checkId
}

// ── Compact inline status line ───────────────────────────────────────────────

function ToolStatusLine({
  isComplete,
  isError,
  awaitingApproval,
  icon,
  completedIcon,
  loadingLabel,
  completedLabel,
  detail,
}: {
  isComplete: boolean
  isError?: boolean
  /**
   * `true` when the tool is paused on the in-chat approval card. We
   * suppress the row entirely in that case — the ApprovalRequestCard
   * is the real UI and painting "Failed: …" above it would be a lie.
   */
  awaitingApproval?: boolean
  icon?: React.ReactNode
  completedIcon?: React.ReactNode
  loadingLabel: string
  completedLabel: string
  detail?: string | null
}) {
  const chatStopped = useChatStopped()
  if (awaitingApproval && !isError) {
    return null
  }
  // If the chat stream has stopped but this tool never completed, treat it as failed
  const effectiveError = isError || (chatStopped && !isComplete)

  const iconNode = effectiveError ? (
    <CircleAlert className='h-3.5 w-3.5 shrink-0 text-status-orange' />
  ) : isComplete ? (
    (completedIcon ?? (
      <CheckCircle2 className='h-3.5 w-3.5 shrink-0 text-emerald-500' />
    ))
  ) : (
    (icon ?? <Loader2 className='h-3.5 w-3.5 shrink-0 animate-spin' />)
  )

  return (
    <Marker className='py-1 text-xs leading-5'>
      <MarkerIcon>{iconNode}</MarkerIcon>
      <MarkerContent>
        {effectiveError
          ? `Failed: ${loadingLabel}`
          : isComplete
            ? completedLabel
            : loadingLabel}
        {detail ? ` · ${detail}` : ''}
      </MarkerContent>
    </Marker>
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// matrix tool — Control matrix extraction, autofill, gap flagging
// Actions: extract, autofill, getControlDetails, flagGap
// ═══════════════════════════════════════════════════════════════════════════════

const MATRIX_ACTION_LABELS: Record<string, { loading: string; done: string }> =
  {
    extract: {
      loading: 'Extracting control matrix...',
      done: 'Control matrix extracted',
    },
    autofill: {
      loading: 'Auto-filling control evidence...',
      done: 'Control evidence mapped',
    },
    getControlDetails: {
      loading: 'Looking up control details...',
      done: 'Control details loaded',
    },
    flagGap: {
      loading: 'Flagging control gap...',
      done: 'Control gap flagged',
    },
  }

export function MatrixToolMessage({ part }: ToolPartProps) {
  const { isComplete, isError, result, args } = getToolState(part)
  const action = args?.action ?? 'extract'
  const labels = MATRIX_ACTION_LABELS[action] ?? {
    loading: 'Processing matrix...',
    done: 'Matrix processed',
  }

  if (isComplete && (isError || isToolError(isComplete, result))) {
    return (
      <ToolErrorGuidance
        toolName='matrix'
        errorMessage={getErrorMessage(result)}
      />
    )
  }

  // extract: show control count
  let detail: string | null = null
  if (isComplete && result) {
    if (action === 'extract' && result.controlCount) {
      detail = `${result.controlCount} controls`
    } else if (action === 'autofill' && result.controlRef) {
      detail = `${result.controlRef} (${result.claimCount ?? 0} claims)`
    } else if (action === 'getControlDetails' && args?.controlRef) {
      detail = args.controlRef
    } else if (action === 'flagGap' && args?.controlRef) {
      detail = `${args.controlRef}${args.severity ? ` [${args.severity}]` : ''}`
    }
  } else if (!isComplete) {
    if (action === 'autofill' && args?.controlRef) detail = args.controlRef
    if (action === 'getControlDetails' && args?.controlRef)
      detail = args.controlRef
    if (action === 'flagGap' && args?.controlRef) detail = args.controlRef
  }

  return (
    <ToolStatusLine
      isComplete={isComplete}
      isError={isError}
      icon={<Loader2 className='h-3.5 w-3.5 shrink-0 animate-spin' />}
      completedIcon={
        <LayoutGrid className='h-3.5 w-3.5 shrink-0 text-emerald-500' />
      }
      loadingLabel={labels.loading}
      completedLabel={labels.done}
      detail={detail}
    />
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// scheduling tool — Compliance schedules, deadlines, tickets, access reviews
// Actions: createSchedule, accessReview, createTicket, getDeadlines
// ═══════════════════════════════════════════════════════════════════════════════

const SCHEDULING_ACTION_LABELS: Record<
  string,
  { loading: string; done: string }
> = {
  createSchedule: { loading: 'Creating schedule...', done: 'Schedule created' },
  accessReview: {
    loading: 'Running access review...',
    done: 'Access review complete',
  },
  createTicket: { loading: 'Creating ticket...', done: 'Ticket created' },
  getDeadlines: { loading: 'Checking deadlines...', done: 'Deadlines loaded' },
}

export function SchedulingToolMessage({ part }: ToolPartProps) {
  const { isComplete, isError, result, args } = getToolState(part)
  const [expanded, setExpanded] = useState(false)
  const action = args?.action ?? 'getDeadlines'
  const labels = SCHEDULING_ACTION_LABELS[action] ?? {
    loading: 'Processing...',
    done: 'Done',
  }

  if (isComplete && (isError || isToolError(isComplete, result))) {
    return (
      <ToolErrorGuidance
        toolName='scheduling'
        errorMessage={getErrorMessage(result)}
      />
    )
  }

  // getDeadlines: show summary with expandable overdue/stale items
  if (action === 'getDeadlines' && isComplete && result) {
    const overdueCount = result.overdueTicketCount ?? 0
    const staleCount = result.staleClaimCount ?? 0
    const openCount = result.openTicketCount ?? 0
    const hasIssues = overdueCount > 0 || staleCount > 0
    const overdueTickets: any[] = Array.isArray(result.overdueTickets)
      ? result.overdueTickets
      : []
    const staleClaims: any[] = Array.isArray(result.staleClaims)
      ? result.staleClaims
      : []

    return (
      <div className='text-muted-foreground py-1 text-xs'>
        <button
          type='button'
          onClick={() =>
            (overdueTickets.length > 0 || staleClaims.length > 0) &&
            setExpanded((v) => !v)
          }
          className={`flex w-full items-center gap-2 ${hasIssues ? 'cursor-pointer' : ''}`}
          disabled={!hasIssues}
        >
          {hasIssues ? (
            <CircleAlert className='h-3.5 w-3.5 shrink-0 text-status-orange' />
          ) : (
            <CalendarClock className='h-3.5 w-3.5 shrink-0 text-emerald-500' />
          )}
          <span className='flex-1 text-left'>
            {result.auditCycleLabel ? `${result.auditCycleLabel} · ` : ''}
            {openCount} open ticket{openCount !== 1 ? 's' : ''}
            {overdueCount > 0 ? `, ${overdueCount} overdue` : ''}
            {staleCount > 0
              ? `, ${staleCount} stale claim${staleCount !== 1 ? 's' : ''}`
              : ''}
          </span>
          {hasIssues &&
            (expanded ? (
              <ChevronDown className='text-muted-foreground h-3.5 w-3.5 shrink-0' />
            ) : (
              <ChevronRight className='text-muted-foreground h-3.5 w-3.5 shrink-0' />
            ))}
        </button>
        {expanded && (
          <div className='mt-1.5 ml-5 space-y-1'>
            {overdueTickets.map((t: any, i: number) => (
              <div
                key={i}
                className='text-muted-foreground flex items-start gap-1.5 text-xs'
              >
                <Ticket className='mt-0.5 h-3 w-3 shrink-0 text-red-400' />
                <span>{t.title ?? `Overdue ticket ${i + 1}`}</span>
              </div>
            ))}
            {staleClaims.map((c: any, i: number) => (
              <div
                key={`stale-${i}`}
                className='text-muted-foreground flex items-start gap-1.5 text-xs'
              >
                <AlertTriangle className='mt-0.5 h-3 w-3 shrink-0 text-status-orange' />
                <span>
                  {c.displayCode ?? c.controlRef ?? 'Stale claim'}
                  {c.claimType ? ` (${c.claimType})` : ''}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }

  // createTicket: show ticket title
  let detail: string | null = null
  if (action === 'createTicket') {
    detail = args?.title ?? result?.title ?? null
  } else if (action === 'createSchedule') {
    detail = args?.controlType ?? args?.description ?? null
  } else if (action === 'accessReview' && isComplete && result) {
    const systems = result.connectedSystems ?? args?.connectedSystems
    if (Array.isArray(systems)) detail = systems.join(', ')
  }

  return (
    <ToolStatusLine
      isComplete={isComplete}
      isError={isError}
      icon={<Loader2 className='h-3.5 w-3.5 shrink-0 animate-spin' />}
      completedIcon={
        <CalendarClock className='h-3.5 w-3.5 shrink-0 text-emerald-500' />
      }
      loadingLabel={labels.loading}
      completedLabel={labels.done}
      detail={detail}
    />
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// policies tool — Generate, check, record claims, analyze gaps
// Actions: generate, check, checkConsistency, recordClaim, analyzeGap
// ═══════════════════════════════════════════════════════════════════════════════

const POLICIES_ACTION_LABELS: Record<
  string,
  { loading: string; done: string }
> = {
  generate: { loading: 'Generating policy...', done: 'Policy generated' },
  getContent: { loading: 'Loading policy...', done: 'Policy loaded' },
  check: {
    loading: 'Checking policy quality...',
    done: 'Policy quality checked',
  },
  checkConsistency: {
    loading: 'Verifying evidence backs up policy claims...',
    done: 'Evidence-to-claim verification complete',
  },
  recordClaim: { loading: 'Recording claim...', done: 'Claim recorded' },
  analyzeGap: {
    loading: 'Analyzing coverage gaps...',
    done: 'Gap analysis complete',
  },
  list: {
    loading: 'Loading policies...',
    done: 'Policies loaded',
  },
}

export function PoliciesToolMessage({
  part,
  isLastAssistantMessage = false,
}: ToolPartProps) {
  const { isComplete, isError, result, args } = getToolState(part)
  const [expanded, setExpanded] = useState(false)
  const action = args?.action ?? 'generate'
  const labels = POLICIES_ACTION_LABELS[action] ?? {
    loading: 'Processing policy...',
    done: 'Policy processed',
  }

  if (isComplete && (isError || isToolError(isComplete, result))) {
    return (
      <ToolErrorGuidance
        toolName='policies'
        errorMessage={getErrorMessage(result)}
        errorCode={result?.errorCode}
        toolAction={action}
      />
    )
  }

  const policySlug = args?.policySlug

  if (action === 'generate') {
    return (
      <PoliciesGenerateToolMessage
        part={part}
        isLastAssistantMessage={isLastAssistantMessage}
      />
    )
  }

  // analyzeGap: show coverage stats with expandable uncovered blocks
  if (action === 'analyzeGap' && isComplete && result) {
    const total = result.totalBlocks ?? 0
    const covered = result.coveredBlocks ?? 0
    const uncovered: any[] = Array.isArray(result.uncoveredBlocks)
      ? result.uncoveredBlocks
      : []
    const hasUncovered = uncovered.length > 0

    return (
      <div className='text-muted-foreground py-1 text-xs'>
        <button
          type='button'
          onClick={() => hasUncovered && setExpanded((v) => !v)}
          className={`flex w-full items-center gap-2 ${hasUncovered ? 'cursor-pointer' : ''}`}
          disabled={!hasUncovered}
        >
          {hasUncovered ? (
            <CircleAlert className='h-3.5 w-3.5 shrink-0 text-status-orange' />
          ) : (
            <CheckCircle2 className='h-3.5 w-3.5 shrink-0 text-emerald-500' />
          )}
          <span className='flex-1 text-left'>
            {policySlug ? `${policySlug}: ` : ''}Gap analysis: {covered}/
            {total} blocks covered
            {result.claimsCreated ? `, ${result.claimsCreated} claims` : ''}
          </span>
          {hasUncovered &&
            (expanded ? (
              <ChevronDown className='text-muted-foreground h-3.5 w-3.5 shrink-0' />
            ) : (
              <ChevronRight className='text-muted-foreground h-3.5 w-3.5 shrink-0' />
            ))}
        </button>
        {expanded && uncovered.length > 0 && (
          <div className='mt-1.5 ml-5 max-h-[200px] space-y-0.5 overflow-y-auto'>
            {uncovered.slice(0, 20).map((block: any, i: number) => (
              <div
                key={block.blockId ?? i}
                className='text-muted-foreground flex items-start gap-1.5 text-xs'
              >
                <span className='text-muted-foreground/60 w-4 shrink-0 text-right text-[10px]'>
                  {block.type?.[0]?.toUpperCase() ?? '·'}
                </span>
                <span className='truncate'>
                  {block.text?.slice(0, 100) ?? `Block ${i + 1}`}
                </span>
              </div>
            ))}
            {uncovered.length > 20 && (
              <div className='text-muted-foreground/60 ml-5 text-[10px]'>
                ...and {uncovered.length - 20} more
              </div>
            )}
          </div>
        )}
      </div>
    )
  }

  // check: show status
  if (action === 'check' && isComplete && result) {
    const status = result.status ?? result.quality
    if (status === 'PASS') {
      return (
        <ToolStatusLine
          isComplete={true}
          completedIcon={
            <CheckCircle2 className='h-3.5 w-3.5 shrink-0 text-emerald-500' />
          }
          loadingLabel={labels.loading}
          completedLabel={`Policy quality: PASS${policySlug ? ` (${policySlug})` : ''}`}
        />
      )
    }
  }

  // recordClaim: compact inline
  if (action === 'recordClaim') {
    return (
      <div className='text-muted-foreground flex items-center gap-2 py-1 text-xs'>
        {isComplete ? (
          <Shield className='h-3.5 w-3.5 shrink-0 text-emerald-500' />
        ) : (
          <Loader2 className='h-3.5 w-3.5 shrink-0 animate-spin' />
        )}
        <span className='truncate'>
          {isComplete ? 'Recorded' : 'Recording'}:{' '}
          {args?.claimText?.slice(0, 80) ?? args?.claimType ?? 'claim'}
        </span>
      </div>
    )
  }

  // list: show summary message from result
  if (action === 'list' && isComplete && result) {
    const total = result.total ?? 0
    const approved =
      (result.byApproval?.fullyApproved ?? 0) +
      (result.byApproval?.hasUnapprovedChanges ?? 0)
    const msg = `${total} policies found, ${approved} with approved version`
    return (
      <ToolStatusLine
        isComplete={true}
        completedIcon={
          <FileText className='h-3.5 w-3.5 shrink-0 text-emerald-500' />
        }
        loadingLabel={labels.loading}
        completedLabel={msg}
      />
    )
  }

  const detail: string | null = policySlug ?? null

  return (
    <ToolStatusLine
      isComplete={isComplete}
      isError={isError}
      icon={<Loader2 className='h-3.5 w-3.5 shrink-0 animate-spin' />}
      completedIcon={
        <FileText className='h-3.5 w-3.5 shrink-0 text-emerald-500' />
      }
      loadingLabel={labels.loading}
      completedLabel={labels.done}
      detail={detail}
    />
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// program tool — Compliance program state, readiness, timeline, roles
// Actions: getState, getReadiness, getTimeline, getRoles
// ═══════════════════════════════════════════════════════════════════════════════

const PROGRAM_ACTION_LABELS: Record<string, { loading: string; done: string }> =
  {
    getState: {
      loading: 'Loading program state...',
      done: 'Program state loaded',
    },
    getReadiness: {
      loading: 'Assessing readiness...',
      done: 'Readiness assessment loaded',
    },
  }

export function ProgramToolMessage({ part }: ToolPartProps) {
  const { isComplete, isError, result, args } = getToolState(part)
  const action = args?.action ?? 'getState'
  const labels = PROGRAM_ACTION_LABELS[action] ?? {
    loading: 'Loading program data...',
    done: 'Program data loaded',
  }

  if (isComplete && (isError || isToolError(isComplete, result))) {
    return (
      <ToolErrorGuidance
        toolName='program'
        errorMessage={getErrorMessage(result)}
      />
    )
  }

  let detail: string | null = null
  if (isComplete && result) {
    if (action === 'getState') {
      const parts = []
      if (result.policiesGenerated)
        parts.push(`${result.policiesGenerated} policies`)
      if (result.controlsTotal)
        parts.push(
          `${result.controlsAnswered ?? 0}/${result.controlsTotal} controls`
        )
      if (result.openTickets) parts.push(`${result.openTickets} open tickets`)
      if (parts.length) detail = parts.join(', ')
    } else if (action === 'getReadiness' && result.score != null) {
      detail = `${Math.round(result.score * 100)}% ready`
    }
  }

  return (
    <ToolStatusLine
      isComplete={isComplete}
      isError={isError}
      icon={<Loader2 className='h-3.5 w-3.5 shrink-0 animate-spin' />}
      completedIcon={
        <ClipboardCheck className='h-3.5 w-3.5 shrink-0 text-emerald-500' />
      }
      loadingLabel={labels.loading}
      completedLabel={labels.done}
      detail={detail}
    />
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// evidence tool — list/get evidence and fulfillment actions
// Actions: listEvidence, getEvidence, fulfillWithPolicy, fulfillWithDocument,
// fulfillWithReport, submitForReview, withdrawSubmission
// ═══════════════════════════════════════════════════════════════════════════════

const EVIDENCE_ACTION_LABELS: Record<
  string,
  { loading: string; done: string }
> = {
  listEvidence: {
    loading: 'Loading evidence...',
    done: 'Evidence loaded',
  },
  getEvidence: {
    loading: 'Retrieving evidence...',
    done: 'Evidence loaded',
  },
  fulfillWithPolicy: {
    loading: 'Attaching policy as evidence...',
    done: 'Policy attached as evidence',
  },
  fulfillWithDocument: {
    loading: 'Adding document as evidence...',
    done: 'Added document as evidence',
  },
  fulfillWithReport: {
    loading: 'Generating report evidence...',
    done: 'Report attached as evidence',
  },
  submitForReview: {
    loading: 'Submitting evidence for review...',
    done: 'Evidence submitted for review',
  },
  withdrawSubmission: {
    loading: 'Withdrawing evidence submission...',
    done: 'Evidence submission withdrawn',
  },
}

export function EvidenceToolMessage({ part }: ToolPartProps) {
  const queryClient = useQueryClient()
  const { isComplete, isError, result, args } = getToolState(part)
  const action = args?.action ?? 'listEvidence'
  const labels = EVIDENCE_ACTION_LABELS[action] ?? {
    loading: 'Loading evidence...',
    done: 'Evidence loaded',
  }

  // Invalidate test detail + artifact queries when chat attaches evidence.
  // ArtifactsSection reads linked documents/files through separate queries,
  // so refreshing only getStatusById leaves the evidence card stale until reload.
  useEffect(() => {
    if (
      (action === 'fulfillWithPolicy' ||
        action === 'fulfillWithReport' ||
        action === 'fulfillWithDocument' ||
        action === 'submitForReview' ||
        action === 'withdrawSubmission') &&
      isComplete &&
      !isError &&
      result?.testId
    ) {
      const statusId = result.testId
      queryClient.invalidateQueries({
        queryKey: trpc.guidedTemplate.getStatusById.queryKey({ id: statusId }),
      })
      queryClient.invalidateQueries({
        queryKey: trpc.evidenceDocLink.list.queryKey({ statusId }),
      })
      queryClient.invalidateQueries({
        queryKey: trpc.evidenceFile.list.queryKey({ statusId }),
      })
      // Flip the "Recommended evidence" policy row from "Add as evidence" to
      // "Added" — the manual attach path invalidates this, but the chat path
      // (agent fulfillWithPolicy) previously did not.
      queryClient.invalidateQueries({
        queryKey: trpc.compliance.getRecommendedEvidence.queryKey({ statusId }),
      })
      queryClient.invalidateQueries({
        queryKey: trpc.evidencePack.listForStatus.queryKey({ statusId }),
      })
      queryClient.invalidateQueries({
        queryKey: trpc.evidenceFile.listTicketAttachments.queryKey({
          statusId,
        }),
      })
      queryClient.invalidateQueries({
        queryKey: trpc.audit.getActivityFeed.infiniteQueryKey({
          targetType: 'control_evidence_status',
          targetId: statusId,
        }),
      })
      queryClient.invalidateQueries({
        queryKey: trpc.compliance.getControlsTree.queryKey(),
      })
    }
  }, [action, isComplete, isError, result?.testId, queryClient])

  if (isComplete && (isError || isToolError(isComplete, result))) {
    return (
      <ToolErrorGuidance
        toolName='evidence'
        errorMessage={getErrorMessage(result)}
        errorCode={result?.errorCode}
        toolAction={action}
      />
    )
  }

  let detail: string | null = null
  if (action === 'getEvidence') {
    const scope =
      [args?.controlRef, args?.provider].filter(Boolean).join(' / ') || null
    if (isComplete && result?.autoCollected) {
      detail = [scope, `refreshed ${result.autoCollectedProvider ?? ''}`]
        .filter(Boolean)
        .join(' · ')
    } else if (isComplete && result?.autoCollectSkipped) {
      detail = [scope, result.autoCollectReason].filter(Boolean).join(' · ')
    } else {
      detail = scope
    }
  }

  return (
    <ToolStatusLine
      isComplete={isComplete}
      isError={isError}
      icon={<Loader2 className='h-3.5 w-3.5 shrink-0 animate-spin' />}
      completedIcon={
        <Database className='h-3.5 w-3.5 shrink-0 text-emerald-500' />
      }
      loadingLabel={labels.loading}
      completedLabel={labels.done}
      detail={detail}
    />
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// findings tool (formerly "remediations") — Findings lifecycle. Native
// remediation plan/execute now lives under checksRemedyPrepare /
// checksRemedyValidate / checksRemedyExecute / checksRemedyUndo
// + the legacy proposeRemediation / executeRemediationPlan / undoRemediation shims.
// Actions: triage, search, get, verify, close, update, acceptRisk, falsePositive, proposePlan, validatePlan, executePlan
// ═══════════════════════════════════════════════════════════════════════════════

const REMEDIATION_ACTION_LABELS: Record<
  string,
  { loading: string; done: string }
> = {
  triage: { loading: 'Triaging finding...', done: 'Finding triaged' },
  search: {
    loading: 'Searching findings...',
    done: 'Findings search complete',
  },
  get: {
    loading: 'Loading finding details...',
    done: 'Finding details loaded',
  },
  verify: { loading: 'Verifying remediation...', done: 'Remediation verified' },
  close: { loading: 'Closing finding...', done: 'Finding closed' },
  update: { loading: 'Updating finding...', done: 'Finding updated' },
  acceptRisk: { loading: 'Accepting risk...', done: 'Risk accepted' },
  falsePositive: {
    loading: 'Marking as false positive...',
    done: 'Marked as false positive',
  },
  proposePlan: {
    loading: 'Preparing remediation context...',
    done: 'Remediation context prepared',
  },
  validatePlan: {
    loading: 'Validating remediation plan...',
    done: 'Remediation proposal ready',
  },
  executePlan: {
    loading: 'Executing remediation plan...',
    done: 'Remediation finished',
  },
}

function formatTicketProviderLabel(provider: unknown): string {
  if (typeof provider !== 'string' || provider.length === 0) {
    return 'Ticket'
  }

  if (provider.toLowerCase() === 'github') {
    return 'GitHub issue'
  }

  return `${provider.charAt(0).toUpperCase()}${provider.slice(1)} ticket`
}

function getRemediationCompletedLabel(params: {
  action: string
  result: any
  fallback: string
}): string {
  const { action, result, fallback } = params

  if (action !== 'triage' || !result) {
    return fallback
  }

  switch (result.action) {
    case 'finding_created':
      return result.finding?.ticketId
        ? `${formatTicketProviderLabel(result.finding.ticketProvider)} created`
        : 'Finding created'
    case 'finding_reopened':
      return result.finding?.ticketId
        ? `${formatTicketProviderLabel(result.finding.ticketProvider)} reopened`
        : 'Finding reopened'
    case 'existing_finding_updated':
      return 'Existing finding updated'
    case 'dismissed':
      return 'Finding dismissed'
    case 'finding_created_ticket_failed':
      return 'Finding created'
    default:
      return fallback
  }
}

export function RemediationToolMessage({ part }: ToolPartProps) {
  const { isComplete, isError, result, args } = getToolState(part)
  const action = args?.action ?? 'search'
  const labels = REMEDIATION_ACTION_LABELS[action] ?? {
    loading: 'Processing finding...',
    done: 'Finding processed',
  }
  const completedLabel = getRemediationCompletedLabel({
    action,
    result,
    fallback: labels.done,
  })

  if (isComplete && (isError || isToolError(isComplete, result))) {
    return (
      <ToolErrorGuidance
        toolName='findings'
        errorMessage={getErrorMessage(result)}
        errorCode={result?.errorCode}
        toolAction={action}
      />
    )
  }

  let detail: string | null = null
  if (action === 'search' && isComplete && result) {
    detail = `${result.total ?? result.count ?? 0} results`
  } else if (action === 'triage' && isComplete && result?.finding?.ticketId) {
    detail = `${formatTicketProviderLabel(result.finding.ticketProvider)} ${result.finding.ticketId}`
  } else if (args?.findingKey) {
    detail = args.findingKey
  }

  return (
    <ToolStatusLine
      isComplete={isComplete}
      isError={isError}
      icon={<Loader2 className='h-3.5 w-3.5 shrink-0 animate-spin' />}
      completedIcon={
        <Wrench className='h-3.5 w-3.5 shrink-0 text-emerald-500' />
      }
      loadingLabel={labels.loading}
      completedLabel={completedLabel}
      detail={detail}
    />
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// proposeRemediation / executeRemediationPlan — RFC-148 native remediation flow
// ═══════════════════════════════════════════════════════════════════════════════

function formatJsonPreview(value: unknown): string {
  if (value == null) return 'None'
  if (typeof value === 'string') return value
  try {
    return JSON.stringify(value, null, 2)
  } catch {
    return String(value)
  }
}

function formatRemediationDetail(result: any, args?: any): string | null {
  const parts: string[] = []
  const provider = result?.provider ?? args?.plan?.provider
  if (typeof provider === 'string') parts.push(provider.toUpperCase())
  const checkId = result?.checkId
  if (typeof checkId === 'string') parts.push(checkId)
  return parts.length > 0 ? parts.join(' · ') : null
}

export function ProposeRemediationToolMessage({ part }: ToolPartProps) {
  const { isComplete, isError, result, args } = getToolState(part)
  const [expanded, setExpanded] = useState(false)
  const action = args?.action ?? 'prepare'

  if (isComplete && (isError || isToolError(isComplete, result))) {
    return (
      <ToolErrorGuidance
        toolName='proposeRemediation'
        errorMessage={getErrorMessage(result)}
        errorCode={result?.errorCode}
      />
    )
  }

  if (!isComplete) {
    return (
      <ToolStatusLine
        isComplete={false}
        icon={<Loader2 className='h-3.5 w-3.5 shrink-0 animate-spin' />}
        completedIcon={
          <Wrench className='h-3.5 w-3.5 shrink-0 text-emerald-500' />
        }
        loadingLabel={
          action === 'prepare'
            ? 'Exploring remediation...'
            : 'Validating remediation plan...'
        }
        completedLabel='Remediation plan ready'
        detail={formatRemediationDetail(null, args)}
      />
    )
  }

  if (action === 'prepare') {
    return (
      <ToolStatusLine
        isComplete
        completedIcon={
          <Wrench className='h-3.5 w-3.5 shrink-0 text-emerald-500' />
        }
        loadingLabel='Exploring remediation...'
        completedLabel='Explored remediation'
        detail={formatRemediationDetail(result)}
      />
    )
  }

  const executionMode = result?.executionMode as string | undefined
  const isManualOnly = executionMode === 'manual_only'
  const isBlocked = executionMode === 'blocked'
  const toneClasses = isBlocked
    ? 'border-status-red-border-subtle bg-status-red-bg'
    : isManualOnly
      ? 'border-status-orange-border bg-status-orange-bg'
      : 'border-status-blue-border bg-status-blue-bg'
  const titleClasses = isBlocked
    ? 'text-status-red-strong'
    : isManualOnly
      ? 'text-status-orange-strong'
      : 'text-status-blue-strong'
  const bodyClasses = isBlocked
    ? 'text-status-red-strong'
    : isManualOnly
      ? 'text-status-orange-strong'
      : 'text-status-blue-strong'

  return (
    <div className={`rounded-md border px-3 py-2 text-xs ${toneClasses}`}>
      <button
        type='button'
        onClick={() => setExpanded((v) => !v)}
        className='flex w-full items-start gap-2 text-left'
      >
        <Wrench className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${titleClasses}`} />
        <div className='min-w-0 flex-1'>
          <div className={`font-medium ${titleClasses}`}>
            Remediation plan ready
          </div>
          <div className={bodyClasses}>
            {result?.provider
              ? `${String(result.provider).toUpperCase()} · `
              : ''}
            {result?.checkId ? String(result.checkId) : 'Native check'}
            {result?.riskLevel ? ` · ${String(result.riskLevel)} risk` : ''}
            {executionMode ? ` · ${executionMode.replace(/_/g, ' ')}` : ''}
          </div>
        </div>
        {expanded ? (
          <ChevronDown className='text-muted-foreground h-3.5 w-3.5 shrink-0' />
        ) : (
          <ChevronRight className='text-muted-foreground h-3.5 w-3.5 shrink-0' />
        )}
      </button>

      {expanded && (
        <div className='mt-2 space-y-2'>
          {result?.plan?.explanation && (
            <p className={bodyClasses}>{String(result.plan.explanation)}</p>
          )}
          {result?.executionReason && (
            <p className={bodyClasses}>{String(result.executionReason)}</p>
          )}
          {Array.isArray(result?.plan?.steps) &&
            result.plan.steps.length > 0 && (
              <div className='space-y-1'>
                <div className={`font-medium ${titleClasses}`}>Steps</div>
                {result.plan.steps.map((step: any, index: number) => (
                  <div
                    key={`${step?.adapterMethod ?? index}`}
                    className={bodyClasses}
                  >
                    {index + 1}. {step?.adapterMethod ?? 'Unknown method'}
                  </div>
                ))}
              </div>
            )}
          <div className='grid gap-2 md:grid-cols-2'>
            <div>
              <div className={`font-medium ${titleClasses}`}>Current state</div>
              <pre
                className={`mt-1 max-h-32 overflow-auto rounded-sm border border-border bg-card/60 p-2 font-mono text-[10px] whitespace-pre-wrap ${bodyClasses}`}
              >
                {formatJsonPreview(result?.plan?.currentState)}
              </pre>
            </div>
            <div>
              <div className={`font-medium ${titleClasses}`}>
                Proposed state
              </div>
              <pre
                className={`mt-1 max-h-32 overflow-auto rounded-sm border border-border bg-card/60 p-2 font-mono text-[10px] whitespace-pre-wrap ${bodyClasses}`}
              >
                {formatJsonPreview(result?.plan?.proposedState)}
              </pre>
            </div>
          </div>
          {Array.isArray(result?.plan?.sideEffects) &&
            result.plan.sideEffects.length > 0 && (
              <div className='space-y-1'>
                <div className={`font-medium ${titleClasses}`}>
                  Side effects
                </div>
                {result.plan.sideEffects.map(
                  (effect: string, index: number) => (
                    <div key={`${effect}-${index}`} className={bodyClasses}>
                      • {effect}
                    </div>
                  )
                )}
              </div>
            )}
        </div>
      )}
    </div>
  )
}

export function ExecuteRemediationPlanToolMessage({ part }: ToolPartProps) {
  const { isComplete, isError, result, args } = getToolState(part)

  if (isComplete && (isError || isToolError(isComplete, result))) {
    return (
      <ToolErrorGuidance
        toolName='executeRemediationPlan'
        errorMessage={getErrorMessage(result)}
        errorCode={result?.errorCode}
      />
    )
  }

  if (!isComplete) {
    return (
      <ToolStatusLine
        isComplete={false}
        icon={<Loader2 className='h-3.5 w-3.5 shrink-0 animate-spin' />}
        completedIcon={
          <Wrench className='h-3.5 w-3.5 shrink-0 text-emerald-500' />
        }
        loadingLabel='Applying remediation...'
        completedLabel='Remediation applied'
        detail={formatRemediationDetail(null, args)}
      />
    )
  }

  const status = result?.status as string | undefined
  const validationRun = result?.validationRun as
    | {
        runId?: string
        resultStatus?: string | null
        status?: string
      }
    | undefined
  const validationStatus =
    validationRun?.resultStatus ??
    validationRun?.status ??
    (result?.verificationResult?.status as string | undefined)
  const hasFailureStatus =
    status === 'failed' ||
    status === 'partially_applied' ||
    status === 'verification_failed'
  const detail = formatRemediationDetail(result, args)

  const executionId = result?.executionId as string | undefined
  const canUndo =
    !!executionId &&
    (status === 'succeeded' || status === 'partially_applied') &&
    !result?.undoneAt

  return (
    <div className='space-y-2'>
      <ToolStatusLine
        isComplete
        isError={isError || hasFailureStatus}
        completedIcon={
          <Wrench className='h-3.5 w-3.5 shrink-0 text-emerald-500' />
        }
        loadingLabel='Applying remediation...'
        completedLabel={
          status === 'validation_pending'
            ? 'Applied remediation, validation pending'
            : status === 'verification_failed'
              ? 'Applied remediation, validation failed'
              : status === 'succeeded'
                ? 'Applied remediation'
                : status === 'failed'
                  ? 'Remediation failed'
                  : 'Applied remediation'
        }
        detail={detail}
      />
      {canUndo && executionId ? (
        <RemediationUndoButton executionId={executionId} />
      ) : null}
    </div>
  )
}

function RemediationUndoButton({ executionId }: { executionId: string }) {
  const [undone, setUndone] = useState(false)
  const undoMutation = useMutation(
    trpc.remediation.undo.mutationOptions() as any
  )

  if (undone || undoMutation.data?.status === 'succeeded') {
    return (
      <div className='text-muted-foreground rounded-md border border-border bg-background px-3 py-2 text-[11px]'>
        Remediation undone.
      </div>
    )
  }

  return (
    <div className='flex items-center gap-2'>
      <Button
        variant='outline'
        size='sm'
        className='h-7 text-[11px]'
        disabled={undoMutation.isPending}
        onClick={() => {
          undoMutation.mutate(
            { executionId },
            {
              onSuccess: (data: any) => {
                if (data?.status === 'succeeded') {
                  setUndone(true)
                  toast.success('Remediation undone')
                } else {
                  toast.error(
                    `Undo ${data?.status ?? 'failed'}${
                      data?.errorMessage ? `: ${data.errorMessage}` : ''
                    }`
                  )
                }
              },
              onError: (err: any) => {
                toast.error(err?.message ?? 'Undo failed')
              },
            }
          )
        }}
      >
        {undoMutation.isPending ? (
          <Loader2 className='mr-1 h-3 w-3 animate-spin' />
        ) : null}
        Undo
      </Button>
      <span className='text-muted-foreground text-[10px]'>
        Replays the captured before-state. Some operations cannot be fully
        reversed.
      </span>
    </div>
  )
}

export function UndoRemediationToolMessage({ part }: ToolPartProps) {
  const { isComplete, isError, result } = getToolState(part)

  if (isComplete && (isError || isToolError(isComplete, result))) {
    return (
      <ToolErrorGuidance
        toolName='undoRemediation'
        errorMessage={getErrorMessage(result)}
        errorCode={result?.errorCode}
      />
    )
  }

  const detail = formatRemediationDetail(result)

  return (
    <ToolStatusLine
      isComplete={isComplete}
      isError={isError}
      icon={<Loader2 className='h-3.5 w-3.5 shrink-0 animate-spin' />}
      completedIcon={
        <Wrench className='h-3.5 w-3.5 shrink-0 text-emerald-500' />
      }
      loadingLabel='Undoing remediation...'
      completedLabel={
        result?.status === 'succeeded'
          ? 'Undid remediation'
          : result?.status === 'failed'
            ? 'Undo failed'
            : 'Undid remediation'
      }
      detail={detail}
    />
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// scanning tool — Cloud/code scanning, drift comparison
// Actions: repoSettings, cloud, code, recordEvidence, compareDrift
// ═══════════════════════════════════════════════════════════════════════════════

const SCANNING_ACTION_LABELS: Record<
  string,
  { loading: string; done: string }
> = {
  repoSettings: {
    loading: 'Checking repo settings...',
    done: 'Repo settings checked',
  },
  cloud: { loading: 'Running cloud scan...', done: 'Cloud scan complete' },
  code: { loading: 'Running code scan...', done: 'Code scan complete' },
  recordEvidence: {
    loading: 'Recording scan evidence...',
    done: 'Scan evidence recorded',
  },
  compareDrift: {
    loading: 'Comparing configuration drift...',
    done: 'Drift comparison complete',
  },
}

export function ScanningToolMessage({ part }: ToolPartProps) {
  const { isComplete, isError, result, args } = getToolState(part)
  const action = args?.action ?? 'cloud'
  const labels = SCANNING_ACTION_LABELS[action] ?? {
    loading: 'Scanning...',
    done: 'Scan complete',
  }

  if (isComplete && (isError || isToolError(isComplete, result))) {
    return (
      <ToolErrorGuidance
        toolName='scanning'
        errorMessage={getErrorMessage(result)}
        errorCode={result?.errorCode}
        toolAction={action}
      />
    )
  }

  let detail: string | null = null
  if (action === 'cloud' && args?.provider) detail = args.provider
  if (action === 'code' && args?.repoPath) detail = args.repoPath
  if (action === 'compareDrift' && isComplete && result) {
    detail = result.baselineMissing
      ? 'no baseline'
      : result.drift
        ? 'changes detected'
        : 'no changes'
  }

  return (
    <ToolStatusLine
      isComplete={isComplete}
      isError={isError}
      icon={<Loader2 className='h-3.5 w-3.5 shrink-0 animate-spin' />}
      completedIcon={
        action === 'cloud' ? (
          <Cloud className='h-3.5 w-3.5 shrink-0 text-emerald-500' />
        ) : (
          <Code2 className='h-3.5 w-3.5 shrink-0 text-emerald-500' />
        )
      }
      loadingLabel={labels.loading}
      completedLabel={labels.done}
      detail={detail}
    />
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// cis tool — CIS Benchmark lookups
// Actions: lookup
// ═══════════════════════════════════════════════════════════════════════════════

export function CisToolMessage({ part }: ToolPartProps) {
  const { isComplete, isError, result, args } = getToolState(part)

  if (isComplete && (isError || isToolError(isComplete, result))) {
    return (
      <ToolErrorGuidance
        toolName='cis'
        errorMessage={getErrorMessage(result)}
      />
    )
  }

  return (
    <ToolStatusLine
      isComplete={isComplete}
      isError={isError}
      icon={<Loader2 className='h-3.5 w-3.5 shrink-0 animate-spin' />}
      completedIcon={
        <Shield className='h-3.5 w-3.5 shrink-0 text-emerald-500' />
      }
      loadingLabel='Looking up CIS benchmark...'
      completedLabel='CIS benchmark loaded'
      detail={args?.cisRef ?? args?.controlRef ?? null}
    />
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// claims tool — Policy claim management
// Actions: list, find, record, edit, delete
// ═══════════════════════════════════════════════════════════════════════════════

export function ClaimsToolMessage({ part }: ToolPartProps) {
  const { isComplete, isError, result, args } = getToolState(part)
  const action = args?.action ?? 'list'

  if (isComplete && (isError || isToolError(isComplete, result))) {
    return (
      <ToolErrorGuidance
        toolName='claims'
        errorMessage={getErrorMessage(result)}
      />
    )
  }

  const actionLabels: Record<string, { loading: string; completed: string }> = {
    list: {
      loading: 'Listing claims...',
      completed: 'Claims listed',
    },
    find: {
      loading: 'Looking up claim...',
      completed: 'Claim found',
    },
    record: {
      loading: 'Recording claim...',
      completed: 'Claim recorded',
    },
    edit: {
      loading: 'Updating claim...',
      completed: 'Claim updated',
    },
    delete: {
      loading: 'Deleting claim...',
      completed: 'Claim deleted',
    },
  }

  const labels = actionLabels[action] || actionLabels.list
  const detail = args?.policySlug || args?.claimId || null

  return (
    <ToolStatusLine
      isComplete={isComplete}
      isError={isError}
      icon={<Loader2 className='h-3.5 w-3.5 shrink-0 animate-spin' />}
      completedIcon={
        <CheckCircle2 className='h-3.5 w-3.5 shrink-0 text-emerald-500' />
      }
      loadingLabel={labels.loading}
      completedLabel={labels.completed}
      detail={detail}
    />
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// ui tool — Page content, present options
// Actions: presentOptions, getPageContent
// ═══════════════════════════════════════════════════════════════════════════════

export function UiToolMessage({ part }: ToolPartProps) {
  const { isComplete, isError, result, args } = getToolState(part)
  const action = args?.action ?? 'getPageContent'

  if (isComplete && (isError || isToolError(isComplete, result))) {
    return (
      <ToolErrorGuidance toolName='ui' errorMessage={getErrorMessage(result)} />
    )
  }

  if (action === 'presentOptions' || action === 'presentQuestion') {
    if (isComplete && !isError) {
      return <PresentOptionsMessage part={part} />
    }
    return (
      <ToolStatusLine
        isComplete={isComplete}
        isError={isError}
        loadingLabel='Preparing options...'
        completedLabel='Options presented'
      />
    )
  }

  return (
    <ToolStatusLine
      isComplete={isComplete}
      isError={isError}
      icon={<Loader2 className='h-3.5 w-3.5 shrink-0 animate-spin' />}
      completedIcon={<Eye className='h-3.5 w-3.5 shrink-0 text-emerald-500' />}
      loadingLabel='Reading page content...'
      completedLabel='Page content loaded'
    />
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// risks tool — Risk register queries and detail lookups
// Actions: stats, list, get, search, update
// ═══════════════════════════════════════════════════════════════════════════════

export function RisksToolMessage({ part }: ToolPartProps) {
  const { isComplete, isError, result, args } = getToolState(part)
  const action = args?.action ?? 'list'

  if (isComplete && (isError || isToolError(isComplete, result))) {
    return (
      <ToolErrorGuidance
        toolName='risks'
        errorMessage={getErrorMessage(result)}
      />
    )
  }

  let loadingLabel = 'Reviewing risks...'
  let completedLabel = 'Risk data loaded'
  let detail: string | null = null

  switch (action) {
    case 'stats':
      loadingLabel = 'Summarizing risk register...'
      completedLabel = 'Risk register summary loaded'
      detail =
        isComplete && typeof result?.total === 'number'
          ? `${result.total} total risks`
          : null
      break
    case 'get':
      loadingLabel = 'Loading risk details...'
      completedLabel = 'Risk details loaded'
      detail = result?.displayId ?? args?.riskId ?? null
      break
    case 'list':
    case 'search':
      loadingLabel = 'Reviewing matching risks...'
      completedLabel =
        isComplete && typeof result?.count === 'number'
          ? `Loaded ${result.count} risk${result.count === 1 ? '' : 's'}`
          : 'Risk list loaded'
      detail =
        args?.riskRating ??
        args?.status ??
        args?.category ??
        args?.ownerLabel ??
        null
      break
    case 'update':
      loadingLabel = 'Updating risk...'
      completedLabel = 'Risk updated'
      detail = args?.riskId ?? result?.displayId ?? null
      break
    default:
      detail = action
      break
  }

  return (
    <ToolStatusLine
      isComplete={isComplete}
      isError={isError}
      icon={<Loader2 className='h-3.5 w-3.5 shrink-0 animate-spin' />}
      completedIcon={
        <AlertTriangle className='h-3.5 w-3.5 shrink-0 text-emerald-500' />
      }
      loadingLabel={loadingLabel}
      completedLabel={completedLabel}
      detail={detail}
    />
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// checks tool — Native compliance check registry: lookup, run, scan, remedy
// ═══════════════════════════════════════════════════════════════════════════════

export function ChecksToolMessage({ part }: ToolPartProps) {
  const { isComplete, isError, result, args } = getToolState(part)
  const action = args?.action ?? 'listChecks'

  if (isComplete && (isError || isToolError(isComplete, result))) {
    return (
      <ToolErrorGuidance
        toolName='checks'
        errorMessage={getErrorMessage(result)}
      />
    )
  }

  let loadingLabel = 'Looking at native checks...'
  let completedLabel = 'Native checks loaded'
  let detail: string | null = null

  switch (action) {
    case 'getCheck':
      loadingLabel = 'Looking up check definition...'
      completedLabel = 'Check definition loaded'
      detail = args?.checkId ?? null
      break
    case 'listChecks':
      loadingLabel = 'Browsing native checks...'
      completedLabel =
        isComplete && typeof result?.count === 'number'
          ? `Found ${result.count} check${result.count === 1 ? '' : 's'}`
          : 'Checks loaded'
      detail = args?.provider ?? args?.controlRef ?? null
      break
    case 'linkedTests':
      loadingLabel = 'Finding linked tests...'
      completedLabel =
        isComplete && typeof result?.count === 'number'
          ? `Found ${result.count} linked test${result.count === 1 ? '' : 's'}`
          : 'Linked tests loaded'
      detail = args?.checkId ?? null
      break
    case 'history':
      loadingLabel = 'Reading check history...'
      completedLabel = 'Check history loaded'
      detail = args?.checkId ?? null
      break
    case 'run':
      loadingLabel = 'Running check...'
      completedLabel = 'Check complete'
      detail = args?.checkId ?? null
      break
    case 'scan':
      loadingLabel = 'Scanning checks...'
      completedLabel = 'Check scan complete'
      detail = Array.isArray(args?.checkIds)
        ? `${args.checkIds.length} check${args.checkIds.length === 1 ? '' : 's'}`
        : (args?.provider ?? null)
      break
    case 'remedy':
      loadingLabel = 'Running native remediation...'
      completedLabel = 'Remediation step complete'
      detail = args?.phase ?? args?.checkId ?? null
      break
    case 'cloud':
      loadingLabel = 'Scanning cloud provider...'
      completedLabel = 'Cloud scan complete'
      detail = args?.provider ?? args?.checkId ?? null
      break
    case 'code':
      loadingLabel = 'Scanning code repository...'
      completedLabel = 'Code scan complete'
      detail = args?.repo ?? null
      break
    case 'saveFinding':
      loadingLabel = 'Saving finding...'
      completedLabel = 'Finding saved'
      detail = args?.checkId ?? null
      break
    case 'detectDrift':
      loadingLabel = 'Detecting drift...'
      completedLabel = 'Drift report ready'
      detail = args?.checkId ?? null
      break
    default:
      detail = action
      break
  }

  return (
    <ToolStatusLine
      isComplete={isComplete}
      isError={isError}
      icon={<Loader2 className='h-3.5 w-3.5 shrink-0 animate-spin' />}
      completedIcon={
        <Shield className='h-3.5 w-3.5 shrink-0 text-emerald-500' />
      }
      loadingLabel={loadingLabel}
      completedLabel={completedLabel}
      detail={detail}
    />
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// artifacts tool — Persist agent-produced output (CSV/JSON/markdown) to storage
// ═══════════════════════════════════════════════════════════════════════════════

export function ArtifactsToolMessage({ part }: ToolPartProps) {
  const { isComplete, isError, result, args } = getToolState(part)

  if (isComplete && (isError || isToolError(isComplete, result))) {
    return (
      <ToolErrorGuidance
        toolName='artifacts'
        errorMessage={getErrorMessage(result)}
      />
    )
  }

  const filename: string | null =
    (typeof args?.filename === 'string' && args.filename) ||
    (typeof result?.filename === 'string' && result.filename) ||
    null

  return (
    <ToolStatusLine
      isComplete={isComplete}
      isError={isError}
      icon={<Loader2 className='h-3.5 w-3.5 shrink-0 animate-spin' />}
      completedIcon={
        <FileText className='h-3.5 w-3.5 shrink-0 text-emerald-500' />
      }
      loadingLabel='Saving artifact...'
      completedLabel='Artifact saved'
      detail={filename}
    />
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// tickets tool — Provider-agnostic ticket CRUD on the workspace's primary
// ticketing platform (Linear / Jira / GitHub Issues)
// ═══════════════════════════════════════════════════════════════════════════════

export function TicketsToolMessage({ part }: ToolPartProps) {
  const { isComplete, isError, result, args } = getToolState(part)
  const action = args?.action ?? 'list'

  if (isComplete && (isError || isToolError(isComplete, result))) {
    return (
      <ToolErrorGuidance
        toolName='tickets'
        errorMessage={getErrorMessage(result)}
      />
    )
  }

  let loadingLabel = 'Working on tickets...'
  let completedLabel = 'Ticket action complete'
  let detail: string | null = null

  switch (action) {
    case 'list':
      loadingLabel = 'Searching tickets...'
      completedLabel =
        isComplete && typeof result?.count === 'number'
          ? `Found ${result.count} ticket${result.count === 1 ? '' : 's'}`
          : 'Tickets searched'
      detail = args?.query ?? null
      break
    case 'get':
      loadingLabel = 'Loading ticket...'
      completedLabel = 'Ticket loaded'
      detail = args?.ticketId ?? result?.ticketId ?? null
      break
    case 'create':
      loadingLabel = 'Filing ticket...'
      completedLabel =
        result?.status === 'duplicates_found'
          ? 'Duplicate ticket detected'
          : isComplete
            ? 'Ticket filed'
            : 'Ticket filed'
      detail = result?.ticketId ?? args?.title ?? null
      break
    case 'update':
      loadingLabel = 'Updating ticket...'
      completedLabel = 'Ticket updated'
      detail = args?.ticketId ?? result?.ticketId ?? null
      break
    case 'close':
    case 'delete':
      loadingLabel = 'Closing ticket...'
      completedLabel = 'Ticket closed'
      detail = args?.ticketId ?? result?.ticketId ?? null
      break
    default:
      detail = action
      break
  }

  return (
    <ToolStatusLine
      isComplete={isComplete}
      isError={isError}
      icon={<Loader2 className='h-3.5 w-3.5 shrink-0 animate-spin' />}
      completedIcon={
        <Ticket className='h-3.5 w-3.5 shrink-0 text-emerald-500' />
      }
      loadingLabel={loadingLabel}
      completedLabel={completedLabel}
      detail={detail}
    />
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// vendors tool — Vendor register lookup
// ═══════════════════════════════════════════════════════════════════════════════

export function VendorsToolMessage({ part }: ToolPartProps) {
  const { isComplete, isError, result, args } = getToolState(part)
  const action = args?.action ?? 'list'

  if (isComplete && (isError || isToolError(isComplete, result))) {
    return (
      <ToolErrorGuidance
        toolName='vendors'
        errorMessage={getErrorMessage(result)}
      />
    )
  }

  const detail =
    action === 'get'
      ? (result?.displayId ?? args?.vendorId ?? null)
      : (args?.riskTier ?? args?.status ?? args?.category ?? null)

  const completedLabel =
    action === 'get'
      ? 'Vendor details loaded'
      : isComplete && typeof result?.count === 'number'
        ? `Loaded ${result.count} vendor${result.count === 1 ? '' : 's'}`
        : 'Vendor list loaded'

  return (
    <ToolStatusLine
      isComplete={isComplete}
      isError={isError}
      icon={<Loader2 className='h-3.5 w-3.5 shrink-0 animate-spin' />}
      completedIcon={
        <Database className='h-3.5 w-3.5 shrink-0 text-emerald-500' />
      }
      loadingLabel='Reviewing vendors...'
      completedLabel={completedLabel}
      detail={detail}
    />
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// assets tool — Asset inventory lookup and classification
// ═══════════════════════════════════════════════════════════════════════════════

export function AssetsToolMessage({ part }: ToolPartProps) {
  const { isComplete, isError, result, args } = getToolState(part)
  const action = args?.action ?? 'list'

  if (isComplete && (isError || isToolError(isComplete, result))) {
    return (
      <ToolErrorGuidance
        toolName='assets'
        errorMessage={getErrorMessage(result)}
      />
    )
  }

  let loadingLabel = 'Reviewing assets...'
  let completedLabel = 'Asset data loaded'
  let detail: string | null = null

  switch (action) {
    case 'get':
      loadingLabel = 'Loading asset details...'
      completedLabel = 'Asset details loaded'
      detail = result?.displayId ?? args?.assetId ?? null
      break
    case 'stats':
      loadingLabel = 'Summarizing asset inventory...'
      completedLabel = 'Asset inventory summary loaded'
      detail = null
      break
    case 'search':
    case 'list':
      completedLabel =
        isComplete && typeof result?.count === 'number'
          ? `Loaded ${result.count} asset${result.count === 1 ? '' : 's'}`
          : 'Asset list loaded'
      detail = args?.assetType ?? args?.provider ?? args?.criticality ?? null
      break
    case 'analyzeGap':
      loadingLabel = 'Analyzing asset inventory gaps...'
      completedLabel = 'Asset gap analysis loaded'
      break
    case 'create':
      loadingLabel = 'Creating asset...'
      completedLabel = 'Asset created'
      detail = result?.displayId ?? args?.name ?? null
      break
    case 'update':
    case 'classify':
    case 'markReviewed':
      loadingLabel = 'Updating asset...'
      completedLabel = 'Asset updated'
      detail = result?.displayId ?? args?.assetId ?? null
      break
    case 'listProviders':
      loadingLabel = 'Loading asset providers...'
      completedLabel = 'Asset providers loaded'
      break
    case 'syncNow':
      loadingLabel = 'Syncing assets...'
      completedLabel = 'Asset sync complete'
      detail = args?.provider ?? null
      break
  }

  return (
    <ToolStatusLine
      isComplete={isComplete}
      isError={isError}
      icon={<Loader2 className='h-3.5 w-3.5 shrink-0 animate-spin' />}
      completedIcon={
        <Database className='h-3.5 w-3.5 shrink-0 text-emerald-500' />
      }
      loadingLabel={loadingLabel}
      completedLabel={completedLabel}
      detail={detail}
    />
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// integrations tool — Connected integration lookup
// ═══════════════════════════════════════════════════════════════════════════════

export function IntegrationsToolMessage({ part }: ToolPartProps) {
  const { isComplete, isError, result, args } = getToolState(part)
  const action = args?.action ?? 'list'

  if (isComplete && (isError || isToolError(isComplete, result))) {
    return (
      <ToolErrorGuidance
        toolName='integrations'
        errorMessage={getErrorMessage(result)}
      />
    )
  }

  let loadingLabel = 'Checking integrations...'
  let completedLabel = 'Integrations loaded'
  let detail: string | null = null

  if (action === 'requestConnect') {
    const reason =
      typeof args?.reason === 'string' && args.reason.trim().length > 0
        ? args.reason.trim()
        : null
    loadingLabel = reason ? `Set up connection: ${reason}` : 'Set up connection'
    completedLabel = reason
      ? `Set up connection: ${reason}`
      : 'Set up connection'
  } else if (action === 'get') {
    completedLabel = 'Integration details loaded'
    detail = args?.integrationId ?? null
  } else {
    completedLabel =
      isComplete && typeof result?.count === 'number'
        ? `Loaded ${result.count} integration${result.count === 1 ? '' : 's'}`
        : 'Integrations loaded'
    detail = args?.category ?? args?.status ?? null
  }

  return (
    <ToolStatusLine
      isComplete={isComplete}
      isError={isError}
      icon={<Loader2 className='h-3.5 w-3.5 shrink-0 animate-spin' />}
      completedIcon={
        <Github className='h-3.5 w-3.5 shrink-0 text-emerald-500' />
      }
      loadingLabel={loadingLabel}
      completedLabel={completedLabel}
      detail={detail}
    />
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// population tool — User / privileged access collectors
// ═══════════════════════════════════════════════════════════════════════════════

export function PopulationToolMessage({ part }: ToolPartProps) {
  const { isComplete, isError, result, args } = getToolState(part)
  const action = args?.action ?? 'listUsers'

  if (isComplete && (isError || isToolError(isComplete, result))) {
    return (
      <ToolErrorGuidance
        toolName='population'
        errorMessage={getErrorMessage(result)}
      />
    )
  }

  const completedSources = result?.summary?.completedSources
  return (
    <ToolStatusLine
      isComplete={isComplete}
      isError={isError}
      icon={<Loader2 className='h-3.5 w-3.5 shrink-0 animate-spin' />}
      completedIcon={
        <Users className='h-3.5 w-3.5 shrink-0 text-emerald-500' />
      }
      loadingLabel={`Collecting population data...`}
      completedLabel={
        isComplete && typeof completedSources === 'number'
          ? `Population data loaded from ${completedSources} source${completedSources === 1 ? '' : 's'}`
          : `Population data loaded`
      }
      detail={
        Array.isArray(args?.providers) && args.providers.length > 0
          ? args.providers.join(', ')
          : action
      }
    />
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// docs tool — Document search and knowledge base
// Actions: search, answer
// ═══════════════════════════════════════════════════════════════════════════════

export function DocsToolMessage({ part }: ToolPartProps) {
  const { isComplete, isError, result, args } = getToolState(part)
  const action = args?.action ?? 'search'

  if (isComplete && (isError || isToolError(isComplete, result))) {
    return (
      <ToolErrorGuidance
        toolName='docs'
        errorMessage={getErrorMessage(result)}
      />
    )
  }

  const isSearch = action === 'search'
  let detail: string | null = null
  if (isSearch && args?.query) detail = args.query
  if (isSearch && isComplete && result?.total != null)
    detail = `${result.total} results`

  return (
    <ToolStatusLine
      isComplete={isComplete}
      isError={isError}
      icon={<Loader2 className='h-3.5 w-3.5 shrink-0 animate-spin' />}
      completedIcon={
        isSearch ? (
          <Search className='h-3.5 w-3.5 shrink-0 text-emerald-500' />
        ) : (
          <BookOpen className='h-3.5 w-3.5 shrink-0 text-emerald-500' />
        )
      }
      loadingLabel={
        isSearch ? 'Searching docs...' : 'Getting answer from docs...'
      }
      completedLabel={isSearch ? 'Doc search complete' : 'Answer retrieved'}
      detail={detail}
    />
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// documents tool — Vault Documents namespace
// Actions: saveDraft, list, search, getById
// ═══════════════════════════════════════════════════════════════════════════════

const DOCUMENTS_ACTION_LABELS: Record<
  string,
  { loading: string; done: string }
> = {
  saveDraft: { loading: 'Preparing document...', done: 'Document prepared' },
  list: { loading: 'Loading documents...', done: 'Documents loaded' },
  search: {
    loading: 'Searching documents...',
    done: 'Document search complete',
  },
  getById: { loading: 'Loading document...', done: 'Document loaded' },
}

export function DocumentsToolMessage({
  part,
  isLastAssistantMessage,
}: ToolPartProps) {
  const { isComplete, isError, result, args } = getToolState(part)
  const action = args?.action ?? result?.action ?? 'list'

  if (action === 'saveDraft') {
    return (
      <DocumentSaveDraftToolMessage
        part={part}
        toolName='documents'
        isLastAssistantMessage={isLastAssistantMessage}
      />
    )
  }

  if (
    isComplete &&
    (isError || isToolError(isComplete, result) || result?.found === false)
  ) {
    return (
      <ToolErrorGuidance
        toolName='documents'
        errorMessage={getErrorMessage(result) ?? 'Document not found'}
      />
    )
  }

  const labels = DOCUMENTS_ACTION_LABELS[action] ?? {
    loading: 'Working with documents...',
    done: 'Documents loaded',
  }

  let detail: string | null = null
  if (action === 'search') {
    detail = isComplete
      ? `${result?.total ?? 0} results`
      : (args?.query ?? null)
  } else if (action === 'list') {
    detail = isComplete ? `${result?.total ?? 0} documents` : null
  } else if (action === 'getById') {
    detail = result?.document?.title ?? args?.id ?? null
  }

  return (
    <ToolStatusLine
      isComplete={isComplete}
      isError={isError}
      icon={<Loader2 className='h-3.5 w-3.5 shrink-0 animate-spin' />}
      completedIcon={
        <FileText className='h-3.5 w-3.5 shrink-0 text-emerald-500' />
      }
      loadingLabel={labels.loading}
      completedLabel={labels.done}
      detail={detail}
    />
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// saveOnboardingData tool — Save onboarding answers incrementally
// ═══════════════════════════════════════════════════════════════════════════════

export function SaveOnboardingDataToolMessage({ part }: ToolPartProps) {
  const { isComplete, isError, result } = getToolState(part)

  if (isComplete && (isError || isToolError(isComplete, result))) {
    return (
      <ToolErrorGuidance
        toolName='saveOnboardingData'
        errorMessage={getErrorMessage(result)}
      />
    )
  }

  const savedCount = result?.savedFields?.length ?? null
  const detail = savedCount != null ? `${savedCount} fields saved` : null

  return (
    <ToolStatusLine
      isComplete={isComplete}
      isError={isError}
      icon={<Loader2 className='h-3.5 w-3.5 shrink-0 animate-spin' />}
      completedIcon={
        <Database className='h-3.5 w-3.5 shrink-0 text-emerald-500' />
      }
      loadingLabel='Saving your answers...'
      completedLabel='Answers saved'
      detail={detail}
    />
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// completeOnboarding tool — Finalize onboarding
// ═══════════════════════════════════════════════════════════════════════════════

export function CompleteOnboardingToolMessage({ part }: ToolPartProps) {
  const { isComplete, isError, result } = getToolState(part)

  if (isComplete && (isError || isToolError(isComplete, result))) {
    return (
      <ToolErrorGuidance
        toolName='completeOnboarding'
        errorMessage={getErrorMessage(result)}
      />
    )
  }

  return (
    <ToolStatusLine
      isComplete={isComplete}
      isError={isError}
      icon={<Loader2 className='h-3.5 w-3.5 shrink-0 animate-spin' />}
      completedIcon={
        <CheckCircle2 className='h-3.5 w-3.5 shrink-0 text-emerald-500' />
      }
      loadingLabel='Finalizing setup...'
      completedLabel='Setup complete'
    />
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// connectGithub tool — Chat renders a slim trigger bar; clicking opens the
// artifact panel (sidecar) with the full connector UI. Same connector code,
// just a different mount point.
// ═══════════════════════════════════════════════════════════════════════════════

export function ConnectGithubToolMessage({ part }: ToolPartProps) {
  const { isComplete, isError, result } = getToolState(part)

  if (!isComplete) {
    return (
      <ToolStatusLine
        isComplete={false}
        icon={<Loader2 className='h-3.5 w-3.5 shrink-0 animate-spin' />}
        loadingLabel='Checking GitHub...'
        completedLabel=''
      />
    )
  }

  if (isError || isToolError(isComplete, result)) {
    return (
      <ToolErrorGuidance
        toolName='connectGithub'
        errorMessage={getErrorMessage(result)}
      />
    )
  }

  // The interactive affordance lives in Chat's pendingSidecarAction
  // overlay (it replaces the PromptInput). The bubble itself has nothing
  // to render — Vera's surrounding text narrates the prompt, and the
  // overlay button opens the connector panel.
  return null
}

// ─── GithubConnectorPanel ───────────────────────────────────────────────────
// Full connector UI, rendered inside the artifact panel. Self-contained:
// owns its queries, install popup flow, and repo selection state. The chat
// bubble (ConnectGithubToolMessage) just opens this via artifactPanelStateAtom.
interface GithubConnectorPanelProps {
  /** Compliance program ID — required to kick off repo scans directly
   * from the panel. Omit only when the panel is used without scan support. */
  programId?: string
  showSkip?: boolean
}

export function GithubConnectorPanel({
  programId,
  showSkip = true,
}: GithubConnectorPanelProps = {}) {
  const [isInstalling, setIsInstalling] = useState(false)
  const [justConnected, setJustConnected] = useState(false)
  const [scanChosen, setScanChosen] = useState(false)
  // Inline scan state — no longer routed through the chat agent.
  const [scanStatus, setScanStatus] = useState<
    'idle' | 'scanning' | 'done' | 'error'
  >('idle')
  const [scanResults, setScanResults] = useState<
    Array<{ repo: string; ok: boolean; error?: string }>
  >([])
  const triggerScanMutation = useMutation(
    trpc.complianceProgram.triggerCodebaseAnalysis.mutationOptions() as any
  )

  // Same queries as the wizard's connect-repo page
  const isConfiguredQuery = useQuery(trpc.githubApp.isConfigured.queryOptions())
  const isGithubConfigured = isConfiguredQuery.data
  const workspaceId =
    window.location.pathname.match(/workspaces\/([^/]+)/)?.[1] ?? ''

  const { data: installationsData, refetch: refetchInstallations } = useQuery(
    trpc.githubApp.listInstallations.queryOptions()
  )

  const hasGithub = justConnected || (installationsData as any[])?.length > 0

  // Listen for GitHub App installation callback from popup (same as wizard)
  useEffect(() => {
    if (!isInstalling) return

    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return

      if (event.data?.type === 'github-app-installed' && event.data?.success) {
        setIsInstalling(false)
        setJustConnected(true)
        void refetchInstallations()
        // Do NOT auto-dispatch — let user choose scan vs skip from the card buttons
      }

      if (event.data?.type === 'github-app-error') {
        setIsInstalling(false)
      }
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [
    isInstalling,
    workspaceId,
    refetchInstallations,
  ])

  const handleConnect = useCallback(() => {
    if (!isGithubConfigured) return
    setIsInstalling(true)

    const popup = openGitHubInstallPopup(workspaceId)
    if (!popup) {
      setIsInstalling(false)
      return
    }

    // Fallback: if popup is closed without postMessage, reset state
    const checkPopup = setInterval(() => {
      if (popup?.closed) {
        clearInterval(checkPopup)
        setIsInstalling(false)
        void refetchInstallations()
      }
    }, 1000)
  }, [isGithubConfigured, workspaceId, refetchInstallations])

  // Already connected — derive from live query only (no tool result here).
  const alreadyConnected = hasGithub

  // Repo selection state
  const [showRepoSelector, setShowRepoSelector] = useState(false)
  const [selectedRepos, setSelectedRepos] = useState<Set<string>>(new Set())

  // Fetch ALL repos from GitHub App installations (not just workspace-connected ones)
  // This matches the wizard pattern — repos are available immediately after OAuth install
  const installationsForRepos = useQuery({
    ...trpc.githubApp.listInstallations.queryOptions(),
    enabled: alreadyConnected,
    refetchInterval: showRepoSelector ? 3000 : false,
  })
  // Also fetch workspace-connected repos to know which are already linked
  const connectedReposQuery = useQuery({
    ...trpc.githubApp.listConnectedRepositories.queryOptions({ workspaceId }),
    enabled: !!workspaceId && alreadyConnected,
    refetchInterval: showRepoSelector ? 3000 : false,
  })

  // Flatten all repos from all installations
  const allInstallationRepos = useMemo(() => {
    const installations = (installationsForRepos.data ?? []) as Array<{
      id: string
      accountLogin: string
      repositories?: Array<{
        id: string
        fullName: string
        name: string
        private: boolean
      }>
    }>
    return installations.flatMap((inst) =>
      (inst.repositories ?? []).map((r) => ({
        ...r,
        accountLogin: inst.accountLogin,
      }))
    )
  }, [installationsForRepos.data])

  // Use installation repos if available, fall back to connected repos
  const connectedRepos = useMemo(
    () =>
      allInstallationRepos.length > 0
        ? allInstallationRepos
        : ((connectedReposQuery.data ?? []) as Array<{
            id: string
            fullName: string
            private: boolean
          }>),
    [allInstallationRepos, connectedReposQuery.data]
  )

  // Auto-select all repos once when dialog first opens
  const hasAutoSelected = useRef(false)
  useEffect(() => {
    if (
      connectedRepos.length > 0 &&
      showRepoSelector &&
      !hasAutoSelected.current
    ) {
      hasAutoSelected.current = true
      setSelectedRepos(new Set(connectedRepos.map((r) => r.fullName)))
    }
  }, [connectedRepos, showRepoSelector])

  // Filter repos by search
  const [repoSearch, setRepoSearch] = useState('')
  const filteredRepos = useMemo(() => {
    if (!repoSearch) return connectedRepos
    const q = repoSearch.toLowerCase()
    return connectedRepos.filter((r) => r.fullName.toLowerCase().includes(q))
  }, [connectedRepos, repoSearch])

  if (alreadyConnected && !isInstalling) {
    return (
      <div className='my-2 w-full space-y-2'>
        <button
          onClick={handleConnect}
          disabled={!isGithubConfigured}
          className='flex w-full items-center gap-3 rounded-md border border-border bg-card px-3 py-2.5 text-left transition-colors hover:border-border-divider hover:bg-surface-subtle disabled:cursor-default disabled:hover:border-border disabled:hover:bg-surface-subtle'
        >
          <Github className='h-4 w-4 shrink-0 text-text-secondary' />
          <div className='min-w-0 flex-1'>
            <div className='text-[12px] font-medium text-text-secondary'>GitHub</div>
            <div className='text-[10px] text-emerald-600'>
              Connected. Click to manage repos
            </div>
          </div>
          <CheckCircle2 className='h-3.5 w-3.5 shrink-0 text-emerald-500' />
        </button>

        {/* Scan/skip buttons */}
        {!scanChosen && (
          <div className='flex gap-3'>
            <Button
              className='flex-1'
              size='sm'
              onClick={() => setShowRepoSelector(true)}
            >
              <Search className='h-3.5 w-3.5' />
              Scan repositories
            </Button>
            {showSkip && (
              <button
                onClick={() => setScanChosen(true)}
                className='flex items-center justify-center gap-1 rounded-md p-2 text-[12px] text-text-subtle transition-colors hover:bg-surface-subtle hover:text-text-secondary'
              >
                Skip scan
              </button>
            )}
          </div>
        )}

        {/* Inline scan status — replaces the chat-driven scan flow. */}
        {scanStatus !== 'idle' && (
          <div className='rounded-md border border-border bg-surface-subtle px-3 py-2.5'>
            {scanStatus === 'scanning' && (
              <div className='flex items-center gap-2 text-[12px] text-text-body'>
                <Loader2 className='h-3.5 w-3.5 animate-spin text-status-blue' />
                Queuing repository scans…
              </div>
            )}
            {scanStatus === 'done' && (
              <div className='space-y-1'>
                <div className='flex items-center gap-2 text-[12px] font-medium text-text-secondary'>
                  <CheckCircle2 className='h-3.5 w-3.5 text-emerald-500' />
                  Scans queued. Results flow into your policies automatically.
                </div>
                <ul className='pl-5 text-[11px] text-text-subtle'>
                  {scanResults.map((r) => (
                    <li key={r.repo}>
                      {r.repo} {r.ok ? '✓' : `· ${r.error ?? 'failed'}`}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {scanStatus === 'error' && (
              <div className='space-y-1'>
                <div className='flex items-center gap-2 text-[12px] font-medium text-status-red-emphasis'>
                  <CircleAlert className='h-3.5 w-3.5' />
                  Couldn’t start any scans.
                </div>
                <ul className='pl-5 text-[11px] text-text-subtle'>
                  {scanResults.map((r) => (
                    <li key={r.repo}>
                      {r.repo}: {r.error ?? 'failed'}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Repo selection dialog */}
        <Dialog open={showRepoSelector} onOpenChange={setShowRepoSelector}>
          <DialogContent className='bg-card p-0 sm:max-w-lg'>
            <DialogHeader className='border-b border-border px-4 pt-4 pb-3'>
              <DialogTitle className='text-[14px] font-semibold text-text-secondary'>
                Select repositories to scan
              </DialogTitle>
              <DialogDescription className='text-[12px] text-text-subtle'>
                Choose which repositories to scan for compliance evidence:
                branch protection, CI/CD, secrets, and dependency scanning.
              </DialogDescription>
            </DialogHeader>

            <div className='border-b border-border px-4 py-2'>
              <input
                type='text'
                placeholder='Search repositories...'
                value={repoSearch}
                onChange={(e) => setRepoSearch(e.target.value)}
                className='h-8 w-full rounded-md border border-border-divider px-2.5 text-[12px] text-text-secondary placeholder:text-text-placeholder focus:border-ring focus:ring-1 focus:ring-ring focus:outline-none'
              />
            </div>

            <div className='flex items-center justify-between px-4 py-1.5'>
              <span className='text-[11px] text-text-subtle'>
                {selectedRepos.size} of {connectedRepos.length} selected
              </span>
              <button
                onClick={() => {
                  if (selectedRepos.size > 0) {
                    setSelectedRepos(new Set())
                  } else {
                    setSelectedRepos(
                      new Set(connectedRepos.map((r) => r.fullName))
                    )
                  }
                }}
                className='text-[11px] text-cobalt-strong hover:underline'
              >
                {selectedRepos.size > 0 ? 'Deselect all' : 'Select all'}
              </button>
            </div>

            <div className='max-h-[320px] overflow-y-auto px-2'>
              {installationsForRepos.isLoading ||
              connectedReposQuery.isLoading ? (
                <div className='flex items-center justify-center py-8'>
                  <Loader2 className='h-4 w-4 animate-spin text-text-placeholder' />
                </div>
              ) : filteredRepos.length === 0 ? (
                <div className='px-2 py-4 text-center text-[12px] text-text-placeholder'>
                  {repoSearch
                    ? 'No matching repositories.'
                    : 'No repositories found. Click "GitHub Connected" to manage repos.'}
                </div>
              ) : (
                filteredRepos.map((repo) => (
                  <label
                    key={repo.fullName}
                    className='flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-2 hover:bg-surface-subtle'
                  >
                    <input
                      type='checkbox'
                      checked={selectedRepos.has(repo.fullName)}
                      onChange={() => {
                        setSelectedRepos((prev) => {
                          const next = new Set(prev)
                          if (next.has(repo.fullName))
                            next.delete(repo.fullName)
                          else next.add(repo.fullName)
                          return next
                        })
                      }}
                      className='h-3.5 w-3.5 rounded-sm border-border-divider text-cobalt-strong focus:ring-ring/20'
                    />
                    <div className='min-w-0 flex-1'>
                      <span className='block truncate font-mono text-[12px] text-text-secondary'>
                        {repo.fullName}
                      </span>
                    </div>
                    {repo.private && (
                      <span className='shrink-0 text-[10px] text-text-placeholder'>
                        private
                      </span>
                    )}
                  </label>
                ))
              )}
            </div>

            <div className='flex gap-2 border-t border-border px-4 py-3'>
              <button
                onClick={async () => {
                  if (!programId) return
                  const picked = Array.from(selectedRepos)
                  setScanStatus('scanning')
                  setScanResults([])
                  setScanChosen(true)
                  setShowRepoSelector(false)

                  const settled = await Promise.allSettled(
                    picked.map((fullName) =>
                      triggerScanMutation
                        .mutateAsync({
                          programId,
                          repositoryUrl: `https://github.com/${fullName}.git`,
                          branch: 'main',
                          repositoryName: fullName,
                        })
                        .then(() => fullName)
                    )
                  )
                  const results = settled.map((s, i) =>
                    s.status === 'fulfilled'
                      ? { repo: picked[i], ok: true }
                      : {
                          repo: picked[i],
                          ok: false,
                          error: (s.reason as any)?.message ?? String(s.reason),
                        }
                  )
                  setScanResults(results)
                  setScanStatus(results.some((r) => r.ok) ? 'done' : 'error')
                }}
                disabled={selectedRepos.size === 0 || !programId}
                className='flex flex-1 items-center justify-center gap-2 rounded-md bg-primary px-4 py-2 text-[12px] font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50'
              >
                <Search className='h-3.5 w-3.5' />
                Scan {selectedRepos.size}{' '}
                {selectedRepos.size === 1 ? 'repository' : 'repositories'}
              </button>
              <button
                onClick={() => {
                  setShowRepoSelector(false)
                }}
                className='flex items-center justify-center rounded-md border border-border bg-card px-4 py-2 text-[12px] text-text-subtle transition-colors hover:bg-surface-subtle'
              >
                Cancel
              </button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    )
  }

  // Loading state while queries resolve
  if (isConfiguredQuery.isLoading) {
    return (
      <ToolStatusLine
        isComplete={false}
        icon={<Loader2 className='h-3.5 w-3.5 shrink-0 animate-spin' />}
        loadingLabel='Checking GitHub configuration...'
        completedLabel=''
      />
    )
  }

  // GitHub App not configured on server — show info, not a disabled button
  if (isConfiguredQuery.data === false) {
    return (
      <div className='my-2 w-full max-w-sm rounded-md border border-border bg-surface-subtle px-3 py-2.5'>
        <div className='flex items-center gap-3'>
          <Github className='h-4 w-4 shrink-0 text-text-placeholder' />
          <div className='min-w-0 flex-1'>
            <div className='text-[12px] font-medium text-text-secondary'>GitHub</div>
            <div className='text-[10px] text-text-placeholder'>
              Not available. Contact your administrator to enable
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='my-2 w-full max-w-sm'>
      <button
        onClick={handleConnect}
        disabled={isInstalling || !isGithubConfigured}
        className='flex w-full items-center gap-3 rounded-md border border-border bg-card px-3 py-2.5 text-left transition-colors hover:border-border-divider hover:bg-surface-subtle disabled:opacity-50'
      >
        <div className='flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary text-primary-foreground'>
          {isInstalling ? (
            <Loader2 className='h-3.5 w-3.5 animate-spin' />
          ) : (
            <Github className='h-3.5 w-3.5' />
          )}
        </div>
        <div className='min-w-0 flex-1'>
          <div className='text-[12px] font-medium text-text-secondary'>
            {isInstalling ? 'Installing...' : 'Install GitHub App'}
          </div>
          <div className='text-[10px] text-text-placeholder'>
            Grants read access to repository contents and metadata
          </div>
        </div>
        <ArrowRight className='h-3.5 w-3.5 shrink-0 text-cobalt-strong' />
      </button>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// connectCloud tool — Inline provider-specific cloud connection
// ═══════════════════════════════════════════════════════════════════════════════

const CLOUD_PROVIDER_LABELS: Record<string, string> = {
  aws: 'AWS',
  gcp: 'Google Cloud',
  azure: 'Azure',
}

export function ConnectCloudToolMessage({ part }: ToolPartProps) {
  const { isComplete, isError, result, args } = getToolState(part)
  const provider = (args?.provider ?? result?.provider ?? 'aws') as string
  const providerLabel =
    CLOUD_PROVIDER_LABELS[provider] ?? provider.toUpperCase()
  const [justConnected, setJustConnected] = useState(false)

  // AWS credential form state
  const [awsAccessKeyId, setAwsAccessKeyId] = useState('')
  const [awsSecretAccessKey, setAwsSecretAccessKey] = useState('')
  const [awsRegion, setAwsRegion] = useState('us-east-1')
  const [isSaving, setIsSaving] = useState(false)

  // Use the existing cloud connection manager hook
  const {
    connectProviderWithOAuth,
    saveAndTestConnection,
    connectingProviderId,
  } = useScreenataCloudConnectionManager({
    onConnected: (p) => {
      if (p === provider) {
        setJustConnected(true)
        dispatchChatOption(`${providerLabel} connected successfully.`)
      }
    },
  })

  const handleAwsSubmit = useCallback(async () => {
    if (!awsAccessKeyId.trim() || !awsSecretAccessKey.trim()) return
    setIsSaving(true)
    const success = await saveAndTestConnection({
      provider: 'aws',
      authMode: 'credentials',
      accessKeyId: awsAccessKeyId.trim(),
      secretAccessKey: awsSecretAccessKey.trim(),
      region: awsRegion.trim() || 'us-east-1',
    })
    setIsSaving(false)
    if (success) {
      setJustConnected(true)
      dispatchChatOption(`${providerLabel} connected successfully.`)
    }
  }, [
    awsAccessKeyId,
    awsSecretAccessKey,
    awsRegion,
    saveAndTestConnection,
    providerLabel,
  ])

  const handleOAuth = useCallback(() => {
    if (provider === 'gcp' || provider === 'azure') {
      connectProviderWithOAuth(provider as 'gcp' | 'azure')
    }
  }, [provider, connectProviderWithOAuth])

  if (!isComplete) {
    return (
      <ToolStatusLine
        isComplete={false}
        icon={<Loader2 className='h-3.5 w-3.5 shrink-0 animate-spin' />}
        loadingLabel={`Checking ${providerLabel}...`}
        completedLabel=''
      />
    )
  }

  if (isError || isToolError(isComplete, result)) {
    return (
      <ToolErrorGuidance
        toolName='connectCloud'
        errorMessage={getErrorMessage(result)}
      />
    )
  }

  // Already connected
  if (result?.alreadyConnected || justConnected) {
    // RFC-197 Phase D — shared post-connect result; surfaces the reconcile
    // ("activating your checks…") instead of a static "Connected".
    return <ConnectionResult providerLabel={providerLabel} className='my-2' />
  }

  // AWS: credential form
  if (provider === 'aws') {
    return (
      <div className='my-2 w-full max-w-sm rounded-md border border-border bg-card'>
        <div className='flex items-center gap-2 border-b border-border px-3 py-2.5'>
          <Cloud className='h-4 w-4 shrink-0 text-text-secondary' />
          <span className='text-[12px] font-medium text-text-secondary'>
            Connect {providerLabel}
          </span>
        </div>
        <div className='space-y-2.5 px-3 py-3'>
          <div>
            <label className='text-[10px] font-medium text-text-body'>
              Access Key ID
            </label>
            <input
              type='text'
              placeholder='AKIA...'
              value={awsAccessKeyId}
              onChange={(e) => setAwsAccessKeyId(e.target.value)}
              className='mt-0.5 h-7 w-full rounded-md border border-border-divider px-2 text-[11px] text-text-secondary placeholder:text-text-placeholder focus:border-ring focus:outline-none'
            />
          </div>
          <div>
            <label className='text-[10px] font-medium text-text-body'>
              Secret Access Key
            </label>
            <input
              type='password'
              placeholder='AWS secret access key'
              value={awsSecretAccessKey}
              onChange={(e) => setAwsSecretAccessKey(e.target.value)}
              className='mt-0.5 h-7 w-full rounded-md border border-border-divider px-2 text-[11px] text-text-secondary placeholder:text-text-placeholder focus:border-ring focus:outline-none'
            />
          </div>
          <div>
            <label className='text-[10px] font-medium text-text-body'>
              Region
            </label>
            <input
              type='text'
              placeholder='us-east-1'
              value={awsRegion}
              onChange={(e) => setAwsRegion(e.target.value)}
              className='mt-0.5 h-7 w-full rounded-md border border-border-divider px-2 text-[11px] text-text-secondary placeholder:text-text-placeholder focus:border-ring focus:outline-none'
            />
          </div>
        </div>
        <div className='flex items-center gap-2 border-t border-border px-3 py-2.5'>
          <button
            onClick={handleAwsSubmit}
            disabled={
              isSaving || !awsAccessKeyId.trim() || !awsSecretAccessKey.trim()
            }
            className='flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-[11px] font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50'
          >
            {isSaving ? (
              <>
                <Loader2 className='h-3 w-3 animate-spin' />
                Testing...
              </>
            ) : (
              'Save & test connection'
            )}
          </button>
          <button
            onClick={() => {
              dispatchChatOption(
                `Skip ${providerLabel}, I'll connect it later.`
              )
            }}
            className='rounded-md border border-border px-3 py-1.5 text-[11px] text-text-subtle transition-colors hover:bg-surface-subtle'
          >
            Skip
          </button>
        </div>
      </div>
    )
  }

  // GCP / Azure: OAuth button
  const isConnecting = connectingProviderId === provider
  return (
    <div className='my-2 w-full max-w-sm space-y-2'>
      <button
        onClick={handleOAuth}
        disabled={isConnecting}
        className='flex w-full items-center gap-3 rounded-md border border-border bg-card px-3 py-2.5 text-left transition-colors hover:border-border-divider hover:bg-surface-subtle disabled:opacity-50'
      >
        <Cloud className='h-4 w-4 shrink-0 text-text-secondary' />
        <div className='min-w-0 flex-1'>
          <div className='text-[12px] font-medium text-text-secondary'>
            {isConnecting
              ? 'Connecting...'
              : `Connect ${providerLabel} with OAuth`}
          </div>
          <div className='text-[10px] text-text-placeholder'>
            Read-only access for compliance scanning
          </div>
        </div>
        {isConnecting ? (
          <Loader2 className='h-3.5 w-3.5 shrink-0 animate-spin text-status-blue' />
        ) : (
          <ArrowRight className='h-3.5 w-3.5 shrink-0 text-cobalt-strong' />
        )}
      </button>
      <button
        onClick={() => {
          dispatchChatOption(`Skip ${providerLabel}, I'll connect it later.`)
        }}
        className='flex w-full items-center justify-center rounded-md border border-border bg-card px-3 py-1.5 text-[11px] text-text-subtle transition-colors hover:bg-surface-subtle'
      >
        Skip and connect later from dashboard
      </button>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// triggerGithubScan tool — Inline scan progress card
// ═══════════════════════════════════════════════════════════════════════════════

// Scanning is driven from the GithubConnectorPanel sidecar — Chat's
// pendingSidecarAction overlay surfaces the "Open GitHub connector"
// button above the input when this tool is pending. The bubble itself
// renders nothing.
export function TriggerGithubScanToolMessage(_props: ToolPartProps) {
  return null
}
// ═══════════════════════════════════════════════════════════════════════════════
// uploadControlMatrix tool — Inline file upload → parse → confirm dialog
// ═══════════════════════════════════════════════════════════════════════════════

export function UploadControlMatrixToolMessage({ part }: ToolPartProps) {
  const frameworkLabel = formatEnrolledFrameworksLabel(useEnabledFrameworks())
  const { isComplete, isError, result } = getToolState(part)
  const [file, setFile] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)
  const [uploaded, setUploaded] = useState(false)
  const [ingested, setIngested] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [skipped, setSkipped] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const programId = result?.programId
  const frameworkId = result?.frameworkId as string | null | undefined
  const alreadyUploaded = result?.alreadyUploaded

  const { mutateAsync: uploadControlMatrix } = useMutation(
    trpc.complianceProgram.uploadControlMatrix.mutationOptions() as any
  )

  // Poll for ingestion completion via tRPC + refetchInterval
  const shouldPoll = uploaded && !ingested
  const progressQuery = useQuery({
    ...trpc.complianceProgram.getControlMatrixStatus.queryOptions({
      programId: programId ?? '',
      frameworkId: frameworkId ?? undefined,
    }),
    enabled: shouldPoll && !!programId && !!frameworkId,
    refetchInterval: (query) => {
      const data = query.state.data as
        | { controlMatrixIngested?: boolean }
        | undefined
      return data?.controlMatrixIngested ? false : 2000
    },
  })

  useEffect(() => {
    const data = progressQuery.data as
      | { controlMatrixIngested?: boolean }
      | undefined
    if (!data?.controlMatrixIngested || ingested) return
    setIngested(true)
    dispatchChatOption('Control matrix uploaded and parsed successfully.')
  }, [progressQuery.data, ingested])

  const handleFileSelect = useCallback(
    async (selectedFile: File) => {
      if (!programId || !frameworkId) return
      setFile(selectedFile)
      setUploading(true)
      setError(null)

      try {
        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader()
          reader.onload = () => {
            const result = reader.result as string
            resolve(result.split(',')[1] || '')
          }
          reader.onerror = reject
          reader.readAsDataURL(selectedFile)
        })

        await (uploadControlMatrix as any)({
          programId,
          frameworkId,
          filename: selectedFile.name,
          contentType: selectedFile.type || 'application/octet-stream',
          base64Data: base64,
          size: selectedFile.size,
        })

        setUploaded(true)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Upload failed')
      } finally {
        setUploading(false)
      }
    },
    [programId, frameworkId, uploadControlMatrix]
  )

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      const droppedFile = e.dataTransfer.files[0]
      if (droppedFile && /\.(xlsx|xls|csv)$/i.test(droppedFile.name)) {
        handleFileSelect(droppedFile)
      } else {
        setError('Please upload an Excel (.xlsx, .xls) or CSV file')
      }
    },
    [handleFileSelect]
  )

  const handleSkip = useCallback(() => {
    setSkipped(true)
    dispatchChatOption(
      `Skipping control matrix upload, will use default ${frameworkLabel} controls.`
    )
  }, [])

  if (!isComplete) {
    return (
      <ToolStatusLine
        isComplete={false}
        icon={<Loader2 className='h-3.5 w-3.5 shrink-0 animate-spin' />}
        loadingLabel='Preparing control matrix upload...'
        completedLabel=''
      />
    )
  }

  if (isError || isToolError(isComplete, result)) {
    return (
      <ToolErrorGuidance
        toolName='uploadControlMatrix'
        errorMessage={getErrorMessage(result)}
      />
    )
  }

  // Already uploaded from a previous session
  if (alreadyUploaded && !uploaded) {
    return (
      <div className='my-2 flex w-full max-w-sm items-center gap-3 rounded-md border border-border bg-surface-subtle px-3 py-2.5'>
        <FileText className='h-4 w-4 shrink-0 text-text-secondary' />
        <div className='min-w-0 flex-1'>
          <div className='text-[12px] font-medium text-text-secondary'>
            Control Matrix
          </div>
          <div className='text-[10px] text-emerald-600'>
            Already uploaded
            {result?.existingFilename ? `: ${result.existingFilename}` : ''}
          </div>
        </div>
        <CheckCircle2 className='h-3.5 w-3.5 shrink-0 text-emerald-500' />
      </div>
    )
  }

  // Skipped
  if (skipped) {
    return (
      <div className='my-2 flex w-full max-w-sm items-center gap-3 rounded-md border border-border bg-surface-subtle px-3 py-2.5'>
        <FileText className='h-4 w-4 shrink-0 text-text-placeholder' />
        <div className='min-w-0 flex-1'>
          <div className='text-[12px] font-medium text-text-secondary'>
            Control Matrix
          </div>
          <div className='text-[10px] text-text-placeholder'>
            Skipped, using default {frameworkLabel}{' '}
            controls
          </div>
        </div>
      </div>
    )
  }

  // Ingested successfully
  if (ingested) {
    return (
      <div className='my-2 flex w-full max-w-sm items-center gap-3 rounded-md border border-border bg-surface-subtle px-3 py-2.5'>
        <FileText className='h-4 w-4 shrink-0 text-text-secondary' />
        <div className='min-w-0 flex-1'>
          <div className='text-[12px] font-medium text-text-secondary'>
            Control Matrix
          </div>
          <div className='text-[10px] text-emerald-600'>
            Uploaded and parsed: {file?.name ?? 'control matrix'}
          </div>
        </div>
        <CheckCircle2 className='h-3.5 w-3.5 shrink-0 text-emerald-500' />
      </div>
    )
  }

  // Uploaded, waiting for ingestion
  if (uploaded && !ingested) {
    return (
      <div className='my-2 flex w-full max-w-sm items-center gap-3 rounded-md border border-border bg-surface-subtle px-3 py-2.5'>
        <FileText className='h-4 w-4 shrink-0 text-text-secondary' />
        <div className='min-w-0 flex-1'>
          <div className='text-[12px] font-medium text-text-secondary'>
            {file?.name ?? 'Control matrix'}
          </div>
          <div className='flex items-center gap-1.5 text-[10px] text-text-subtle'>
            <Loader2 className='h-3 w-3 animate-spin' />
            Parsing controls...
          </div>
        </div>
      </div>
    )
  }

  // File drop zone
  return (
    <div className='my-2 w-full max-w-sm space-y-2'>
      <div
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        onClick={() => fileInputRef.current?.click()}
        className='flex cursor-pointer flex-col items-center gap-2 rounded-md border border-dashed border-border-divider bg-card px-4 py-5 text-center transition-colors hover:border-border-divider hover:bg-surface-subtle'
      >
        <FileText className='h-6 w-6 text-text-placeholder' />
        <div>
          <div className='text-[12px] font-medium text-text-secondary'>
            {uploading ? 'Uploading...' : 'Drop your control matrix here'}
          </div>
          <div className='text-[10px] text-text-placeholder'>
            Excel (.xlsx, .xls) or CSV from your auditor
          </div>
        </div>
        {uploading && (
          <Loader2 className='h-4 w-4 animate-spin text-status-blue' />
        )}
        <input
          ref={fileInputRef}
          type='file'
          accept='.xlsx,.xls,.csv'
          className='hidden'
          onChange={(e) => {
            const f = e.target.files?.[0]
            if (f) handleFileSelect(f)
          }}
        />
      </div>
      {error && (
        <div className='flex items-center gap-1.5 text-[10px] text-status-red'>
          <CircleAlert className='h-3 w-3 shrink-0' />
          {error}
        </div>
      )}
      <button
        onClick={handleSkip}
        className='text-[11px] text-text-placeholder underline-offset-2 hover:underline'
      >
        Skip, I&apos;ll upload later
      </button>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// showArtifact tool — No visible UI. Opens the artifact panel via Jotai atom.
// See RFC-154 §2.5.
// ═══════════════════════════════════════════════════════════════════════════════

export function ShowArtifactToolMessage({ part }: ToolPartProps) {
  const { isComplete, isError, result, args } = getToolState(part)
  const setArtifactPanelState = useSetAtom(artifactPanelStateAtom)
  const appliedRef = useRef(false)

  useEffect(() => {
    if (!isComplete || isError || appliedRef.current) return
    const source = (result ?? args) as {
      type?: string
      policyDocumentId?: string
      policySlug?: string
      activeFieldId?: string
    }
    if (!source?.type) return

    let nextState: ArtifactPanelState = null
    if (source.type === 'onboarding-answers') {
      nextState = {
        type: 'onboarding-answers',
        activeFieldId: source.activeFieldId,
      }
    } else if (source.type === 'policy' && source.policyDocumentId) {
      nextState = {
        type: 'policy',
        policyDocumentId: source.policyDocumentId,
        policySlug: source.policySlug,
      }
    } else if (source.type === 'integrations') {
      nextState = { type: 'integrations' }
    }
    if (nextState) {
      setArtifactPanelState(nextState)
      appliedRef.current = true
    }
  }, [isComplete, isError, result, args, setArtifactPanelState])

  // No visible UI — this tool is a pure state dispatcher. Return null to
  // keep the chat transcript clean. (The user sees the panel itself slide in.)
  return null
}

// ═══════════════════════════════════════════════════════════════════════════════
// presentCtaButton tool — Inline CTA button for conversational handoffs.
// Either navigates (action='navigate') or opens an artifact (action='openArtifact').
// ═══════════════════════════════════════════════════════════════════════════════

interface CtaButtonArgs {
  label?: string
  variant?: 'primary' | 'secondary'
  action?: 'navigate' | 'openArtifact'
  target?: string
  targetId?: string
}

export function PresentCtaButtonMessage({ part }: ToolPartProps) {
  const { isComplete, isError, result, args } = getToolState(part)
  const navigate = useNavigate()
  const setArtifactPanelState = useSetAtom(artifactPanelStateAtom)

  // Result shape matches args shape — server tool just echoes them back with success:true.
  const buttonSpec: CtaButtonArgs = (result ?? args) as CtaButtonArgs

  const handleClick = useCallback(() => {
    if (!buttonSpec?.action) return
    if (buttonSpec.action === 'navigate' && buttonSpec.target) {
      // Resolve $workspaceId placeholder from the current URL so Vera can
      // produce routes like '/workspaces/$workspaceId/connect-tools'
      // without knowing the id.
      const workspaceId =
        window.location.pathname.match(/workspaces\/([^/]+)/)?.[1]
      const to = buttonSpec.target
      if (to.includes('$workspaceId') && workspaceId) {
        void navigate({
          to: to as any,
          params: { workspaceId },
        })
      } else {
        void navigate({ to: to as any })
      }
      return
    }
    if (buttonSpec.action === 'openArtifact' && buttonSpec.target) {
      if (buttonSpec.target === 'onboarding-answers') {
        setArtifactPanelState({ type: 'onboarding-answers' })
      } else if (buttonSpec.target === 'policy' && buttonSpec.targetId) {
        setArtifactPanelState({
          type: 'policy',
          policyDocumentId: buttonSpec.targetId,
        })
      } else if (buttonSpec.target === 'integrations') {
        setArtifactPanelState({ type: 'integrations' })
      } else if (buttonSpec.target === 'connect-tools') {
        // Agent used openArtifact but connect-tools is a route — navigate.
        const workspaceId =
          window.location.pathname.match(/workspaces\/([^/]+)/)?.[1]
        if (workspaceId) {
          void navigate({
            to: '/workspaces/$workspaceId/connect-tools',
            params: { workspaceId },
          })
        }
      }
    }
  }, [buttonSpec, navigate, setArtifactPanelState])

  if (!isComplete) {
    return (
      <ToolStatusLine
        isComplete={false}
        icon={<Loader2 className='h-3.5 w-3.5 shrink-0 animate-spin' />}
        loadingLabel='Preparing next step…'
        completedLabel=''
      />
    )
  }

  if (isError || !buttonSpec?.label || !buttonSpec?.action) {
    return null
  }

  const isPrimary = (buttonSpec.variant ?? 'primary') === 'primary'

  return (
    <div className='my-2'>
      {isPrimary ? (
        <button
          type='button'
          onClick={handleClick}
          className='inline-flex items-center gap-2 rounded-md bg-primary px-5 py-2.5 text-[13px] font-semibold text-primary-foreground shadow-[0_1px_2px_rgba(63,95,206,0.2)] transition-colors hover:bg-primary/90'
        >
          {buttonSpec.label}
          <ArrowRight className='h-3.5 w-3.5 shrink-0' />
        </button>
      ) : (
        <button
          type='button'
          onClick={handleClick}
          className='text-[12px] text-text-body underline-offset-2 hover:text-text-secondary hover:underline'
        >
          {buttonSpec.label}
        </button>
      )}
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// presentComplianceRoles tool — chat bubble is a slim trigger bar that
// opens the full role editor in the artifact sidecar. Real form lives in
// web/src/components/screenata/ComplianceRolesPanel.tsx. See RFC-154 §0.
// ═══════════════════════════════════════════════════════════════════════════════

export function PresentComplianceRolesToolMessage({ part }: ToolPartProps) {
  const { isComplete, isError, result } = getToolState(part)

  if (!isComplete) {
    return (
      <ToolStatusLine
        isComplete={false}
        icon={<Loader2 className='h-3.5 w-3.5 shrink-0 animate-spin' />}
        loadingLabel='Loading compliance roles...'
        completedLabel=''
      />
    )
  }

  if (isError || isToolError(isComplete, result)) {
    return (
      <ToolErrorGuidance
        toolName='presentComplianceRoles'
        errorMessage={getErrorMessage(result)}
      />
    )
  }

  return (
    <ToolStatusLine
      isComplete
      isError={false}
      icon={<Loader2 className='h-3.5 w-3.5 shrink-0 animate-spin' />}
      completedIcon={
        <UserCheck className='h-3.5 w-3.5 shrink-0 text-emerald-500' />
      }
      loadingLabel='Loading compliance roles...'
      completedLabel='Compliance roles ready to review'
    />
  )
}

// ── RFC-144 / Issue #364: Document save draft card with HITL overlay ─────────

type DraftCardLocalState =
  | { kind: 'pending' }
  | { kind: 'saved'; documentId: string; title: string }
  | { kind: 'discarded' }

const DRAFT_RESOLUTION_KEY_PREFIX = 'screenata:vault-draft:'

function readDraftResolution(draftId: string): DraftCardLocalState {
  if (typeof window === 'undefined') return { kind: 'pending' }
  try {
    const raw = window.localStorage.getItem(
      `${DRAFT_RESOLUTION_KEY_PREFIX}${draftId}`
    )
    if (!raw) return { kind: 'pending' }
    const parsed = JSON.parse(raw) as DraftCardLocalState
    if (parsed?.kind === 'saved' && parsed.documentId && parsed.title)
      return parsed
    if (parsed?.kind === 'discarded') return parsed
  } catch {
    // ignore — corrupt entry
  }
  return { kind: 'pending' }
}

function writeDraftResolution(
  draftId: string,
  resolution: DraftCardLocalState
): void {
  if (typeof window === 'undefined') return
  try {
    if (resolution.kind === 'pending') {
      window.localStorage.removeItem(`${DRAFT_RESOLUTION_KEY_PREFIX}${draftId}`)
    } else {
      window.localStorage.setItem(
        `${DRAFT_RESOLUTION_KEY_PREFIX}${draftId}`,
        JSON.stringify(resolution)
      )
    }
  } catch {
    // localStorage unavailable / quota — ignore
  }
}

export function DocumentSaveDraftToolMessage({
  part,
  toolName = 'documents',
  isLastAssistantMessage = false,
}: ToolPartProps & { toolName?: string }) {
  const { isComplete, isError, result, args } = getToolState(part)
  const workspaceId =
    window.location.pathname.match(/workspaces\/([^/]+)/)?.[1] ?? ''

  const openOverlay = useSetAtom(openMainPanelOverlayAtom)
  const closeOverlay = useSetAtom(closeMainPanelOverlayAtom)
  const setProgrammaticMessage = useSetAtom(chatProgrammaticMessageAtom)

  // Slack channel auto-saves server-side (action === 'saved'); web/email
  // returns action === 'draft' for HITL preview.
  const action: string | undefined = result?.action

  const title: string = result?.title ?? args.title ?? 'Untitled Document'
  const keyDocumentKey: string | undefined = result?.keyDocumentKey ?? args.keyDocumentKey ?? undefined
  const documentId: string | undefined = result?.documentId ?? undefined
  const expectedUpdatedAt: string | undefined = result?.expectedUpdatedAt ?? undefined
  const category: string = result?.category ?? args.category ?? 'OTHER'
  const description: string = result?.description ?? args.description ?? ''
  const content: string = result?.content ?? args.content ?? ''
  const tiptapJson: unknown = result?.tiptapJson ?? args.tiptapJson ?? null
  const controlRefs: string[] =
    result?.controlRefs ?? args.frameworkControlRefs ?? args.controlRefs ?? []
  const controlDefIds: string[] =
    result?.controlDefIds ?? args.controlDefIds ?? []

  const toolCallId: string | undefined =
    part.toolCallId ?? part.toolInvocation?.toolCallId
  // Stable per-tool-call id so reopens don't stack and saved/discarded
  // state survives re-renders.
  const draftId = toolCallId ?? `draft-${title}-${(content ?? '').slice(0, 32)}`

  // Hydrate local resolution state from localStorage so reloads don't
  // re-open a draft the user already saved or discarded. The persisted
  // tool result still says `action: "draft"` (we never mutate the chat
  // message after the fact) — this side-channel records what the user
  // did with it.
  const [localState, setLocalStateRaw] = useState<DraftCardLocalState>(() =>
    readDraftResolution(draftId)
  )
  const setLocalState = useCallback(
    (next: DraftCardLocalState) => {
      setLocalStateRaw(next)
      writeDraftResolution(draftId, next)
    },
    [draftId]
  )

  const isDraft = isComplete && action === 'draft'
  const isAutoSaved = isComplete && action === 'saved'

  const openDraftOverlay = useCallback(() => {
    if (!isDraft) return
    const fireDiscard = () => {
      setLocalState({ kind: 'discarded' })
      setProgrammaticMessage({
        text: `I discarded the draft document "${title}". Don't try to save it again unless I ask.`,
      })
    }
    openOverlay({
      kind: 'vault-document-draft',
      id: draftId,
      // X / ESC are treated as Discard so the agent gets a follow-up
      // message and the conversation doesn't stall waiting for a
      // decision the user implicitly made by closing.
      onDismiss: fireDiscard,
      payload: {
        draftId,
        keyDocumentKey,
        documentId,
        originalDocumentTitle: result?.originalDocumentTitle ?? undefined,
        expectedUpdatedAt,
        title,
        content,
        tiptapJson,
        category,
        description,
        controlRefs,
        controlDefIds,
        sourceChatId: null,
        sourceMessageId: null,
        onSaved: ({ documentId, title: savedTitle }) => {
          setLocalState({ kind: 'saved', documentId, title: savedTitle })
          // Tell the agent so it can continue (e.g. fulfill evidence
          // with the new documentId) without the user having to type.
          setProgrammaticMessage({
            text: `I saved the document "${savedTitle}" to the vault. Document ID: ${documentId}.`,
          })
        },
        onDiscarded: fireDiscard,
      },
    })
  }, [
    isDraft,
    openOverlay,
    draftId,
    keyDocumentKey,
    documentId,
    result?.originalDocumentTitle,
    expectedUpdatedAt,
    title,
    content,
    tiptapJson,
    category,
    description,
    controlRefs,
    controlDefIds,
    setProgrammaticMessage,
  ])

  // Open the saved document read-only in the main-panel preview overlay
  // (instead of navigating away to the document route).
  const openSavedDocOverlay = useCallback(
    (docId: string) => {
      if (!workspaceId) return
      openOverlay({
        kind: 'system-detail',
        id: `system-detail-document-${docId}`,
        source: 'chat',
        payload: {
          entityType: 'document',
          entityId: docId,
          workspaceId,
        },
      })
    },
    [openOverlay, workspaceId]
  )

  // Auto-open overlay once when a fresh draft lands. Skip when this
  // tool's parent message isn't the last assistant message — that
  // means the conversation has moved past this draft (e.g. the user
  // reloaded the page and an older draft is sitting in chat
  // history). The user can still click "Reopen draft" on the card.
  const autoOpenedRef = useRef(false)
  useEffect(() => {
    if (!isDraft || autoOpenedRef.current) return
    if (localState.kind !== 'pending') return
    if (!isLastAssistantMessage) return
    autoOpenedRef.current = true
    openDraftOverlay()
  }, [isDraft, openDraftOverlay, localState.kind, isLastAssistantMessage])

  // Pop the overlay if the chat unmounts this card.
  useEffect(() => {
    return () => {
      if (isDraft) closeOverlay(draftId)
    }
  }, [isDraft, closeOverlay, draftId])

  if (!isComplete) {
    return (
      <ToolStatusLine
        isComplete={false}
        icon={
          <Loader2 className='h-3.5 w-3.5 shrink-0 animate-spin text-status-blue' />
        }
        loadingLabel='Preparing document draft...'
        completedLabel=''
      />
    )
  }

  if (isError || isToolError(isComplete, result)) {
    return (
      <ToolErrorGuidance
        toolName={toolName}
        errorMessage={getErrorMessage(result)}
      />
    )
  }

  const savedDocId =
    localState.kind === 'saved'
      ? localState.documentId
      : isAutoSaved
        ? (result?.documentId as string | undefined)
        : undefined

  const renderHeader = (statusBadge: ReactNode) => (
    <div className='flex items-center gap-2 border-b border-border bg-surface-subtle px-4 py-2.5'>
      <span className='flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-border bg-card text-text-subtle'>
        <FileText className='h-3.5 w-3.5' />
      </span>
      <span className='truncate text-[12px] font-medium text-text-secondary'>
        {localState.kind === 'saved' ? localState.title : title}
      </span>
      <span className='ml-auto'>{statusBadge}</span>
    </div>
  )

  return (
    <div className='my-2 w-full overflow-hidden rounded-md border border-border bg-card'>
      {localState.kind === 'pending' && isDraft && (
        <>
          {renderHeader(
            <span className='inline-flex shrink-0 rounded-sm border border-border bg-card px-2 py-0.5 text-[10px] font-medium text-text-subtle'>
              Draft · awaiting review
            </span>
          )}
          {description && (
            <p className='px-4 py-2 text-[11px] text-text-subtle'>
              {description}
            </p>
          )}
          <div className='flex items-center gap-2 px-4 py-2.5'>
            <button
              type='button'
              onClick={openDraftOverlay}
              className='inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-[11px] font-medium text-text-body transition-colors hover:bg-surface-subtle hover:text-text-secondary'
            >
              <ExternalLink className='h-3 w-3' />
              Reopen draft
            </button>
            <span className='text-[10px] text-text-placeholder'>
              Not yet saved to vault
            </span>
          </div>
        </>
      )}

      {localState.kind === 'saved' && (
        <>
          {renderHeader(null)}
          <div className='flex items-center gap-2 px-4 py-2.5'>
            <span className='text-[11px] text-status-green-strong'>Saved to vault</span>
            {savedDocId && workspaceId && (
              <Button
                type='button'
                variant='ghost'
                size='xxs'
                onClick={() => openSavedDocOverlay(savedDocId)}
                className='px-2 text-[11px] font-medium text-text-body hover:bg-surface-subtle hover:text-text-secondary'
              >
                View
              </Button>
            )}
          </div>
        </>
      )}

      {localState.kind === 'discarded' && (
        <>
          {renderHeader(
            <span className='inline-flex shrink-0 rounded-md border border-border bg-surface-subtle px-2 py-0.5 text-[10px] font-medium text-text-subtle'>
              Discarded
            </span>
          )}
          <div className='px-4 py-2.5 text-[11px] text-text-subtle'>
            Draft was not saved.
          </div>
        </>
      )}

      {/* Slack auto-save path keeps the legacy compact preview. */}
      {isAutoSaved && localState.kind === 'pending' && (
        <>
          {renderHeader(
            <span className='inline-flex shrink-0 rounded-md border border-status-green-border-subtle bg-status-green-bg px-2 py-0.5 text-[10px] font-medium text-status-green-strong'>
              Saved
            </span>
          )}
          {description && (
            <p className='px-4 py-2 text-[11px] text-text-subtle'>
              {description}
            </p>
          )}
          {controlRefs.length > 0 && (
            <div className='px-4 py-2'>
              <span className='text-[10px] text-text-subtle'>Controls: </span>
              <span className='text-[10px] text-text-body'>
                {controlRefs.join(', ')}
              </span>
            </div>
          )}
          {savedDocId && workspaceId && (
            <div className='flex items-center gap-2 px-4 py-2.5'>
              <span className='text-[11px] text-status-green-strong'>
                Saved to vault
              </span>
              <Button
                type='button'
                variant='ghost'
                size='xxs'
                onClick={() => openSavedDocOverlay(savedDocId)}
                className='px-2 text-[11px] font-medium text-cobalt-strong hover:text-cobalt-strong'
              >
                View
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// sendEmail tool — Agent-drafted email with user confirmation
// ═══════════════════════════════════════════════════════════════════════════════

export function SendEmailToolMessage({ part }: ToolPartProps) {
  const { isComplete, isError, result, args } = getToolState(part)
  const sendEmailMutation = useMutation(trpc.emailSend.send.mutationOptions())

  if (!isComplete) {
    return (
      <ToolStatusLine
        isComplete={false}
        icon={<Mail className='h-3.5 w-3.5 text-cobalt-strong' />}
        loadingLabel='Composing email...'
        completedLabel=''
      />
    )
  }

  if (isError || isToolError(isComplete, result)) {
    return (
      <ToolErrorGuidance
        toolName='sendEmail'
        errorMessage={getErrorMessage(result)}
      />
    )
  }

  const to = args.to ?? result?.to ?? ''
  const subject = args.subject ?? result?.subject ?? ''
  const body = args.body ?? result?.body ?? ''
  const controlRefs =
    args.frameworkControlRefs ?? args.controlRefs ?? result?.controlRefs ?? []

  if (sendEmailMutation.isSuccess) {
    return (
      <div className='my-2 w-full max-w-lg rounded-md border border-status-green-border-subtle bg-status-green-bg px-4 py-3'>
        <div className='flex items-center gap-2'>
          <CheckCircle2 className='h-4 w-4 shrink-0 text-status-green-strong' />
          <span className='text-[12px] font-medium text-status-green-strong'>
            Email sent
          </span>
        </div>
        <p className='mt-1 text-[11px] text-status-green-strong'>
          Sent to {to}: "{subject}"
        </p>
      </div>
    )
  }

  return (
    <div className='my-2 w-full max-w-lg overflow-hidden rounded-md border border-border bg-card'>
      <div className='border-b border-border bg-surface-subtle px-4 py-2.5'>
        <div className='flex items-center gap-2'>
          <Mail className='h-4 w-4 shrink-0 text-cobalt-strong' />
          <span className='text-[12px] font-medium text-text-secondary'>
            Email Draft
          </span>
        </div>
      </div>
      <div className='space-y-1.5 px-4 py-2.5'>
        <div className='flex items-baseline gap-2'>
          <span className='text-[10px] font-medium tracking-wide text-text-placeholder uppercase'>
            To
          </span>
          <span className='text-[12px] text-text-secondary'>{to}</span>
        </div>
        <div className='flex items-baseline gap-2'>
          <span className='text-[10px] font-medium tracking-wide text-text-placeholder uppercase'>
            Subject
          </span>
          <span className='text-[12px] font-medium text-text-secondary'>
            {subject}
          </span>
        </div>
      </div>
      <div className='max-h-[200px] overflow-y-auto border-t border-border px-4 py-2.5'>
        <pre className='font-sans text-[11px] leading-relaxed whitespace-pre-wrap text-text-body'>
          {body}
        </pre>
      </div>
      {controlRefs.length > 0 && (
        <div className='border-t border-border px-4 py-2'>
          <span className='text-[10px] text-text-subtle'>Controls: </span>
          <span className='text-[10px] text-text-body'>
            {controlRefs.join(', ')}
          </span>
        </div>
      )}
      <div className='flex items-center justify-end gap-2 border-t border-border px-4 py-2.5'>
        {sendEmailMutation.isError && (
          <span className='text-[10px] text-destructive'>
            Failed to send. Try again
          </span>
        )}
        <button
          onClick={() => sendEmailMutation.mutate({ to, subject, body })}
          disabled={sendEmailMutation.isPending}
          className='flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-[11px] font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50'
        >
          {sendEmailMutation.isPending ? (
            <Loader2 className='h-3 w-3 animate-spin' />
          ) : (
            <Send className='h-3 w-3' />
          )}
          Send Email
        </button>
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// tests tool — List, get, assign, update, complete tests and subtasks
// ═══════════════════════════════════════════════════════════════════════════════

const TESTS_ACTION_LABELS: Record<string, { loading: string; done: string }> = {
  list: { loading: 'Listing tests...', done: 'Tests loaded' },
  get: { loading: 'Loading test details...', done: 'Test details loaded' },
  assign: { loading: 'Assigning test...', done: 'Test assigned' },
  updateStatus: {
    loading: 'Updating test status...',
    done: 'Test status updated',
  },
  complete: { loading: 'Completing test...', done: 'Test completed' },
  setDueDate: { loading: 'Setting due date...', done: 'Due date set' },
  listSubtasks: { loading: 'Loading subtasks...', done: 'Subtasks loaded' },
  assignSubtask: { loading: 'Assigning subtask...', done: 'Subtask assigned' },
  bulkAssign: {
    loading: 'Assigning tests...',
    done: 'Tests assigned',
  },
  convert: { loading: 'Converting test...', done: 'Test converted' },
}

const TESTS_WRITE_ACTIONS = new Set([
  'assign',
  'assignSubtask',
  'bulkAssign',
  'setDueDate',
  'updateStatus',
  'complete',
  'convert',
])

export function TestsToolMessage({ part }: ToolPartProps) {
  const { isComplete, isError, result, args } = getToolState(part)
  const [expanded, setExpanded] = useState(false)
  const queryClient = useQueryClient()
  const action: string = args?.action ?? 'list'
  const labels = TESTS_ACTION_LABELS[action] ?? {
    loading: 'Processing...',
    done: 'Done',
  }

  // Invalidate test/subtask queries after write operations succeed.
  // Extract stable primitives for deps to avoid re-firing on every render
  // (getToolState returns fresh objects each call).
  const isWriteAction = TESTS_WRITE_ACTIONS.has(action)
  const testId =
    result?.test?.id ??
    result?.subtask?.id ??
    args?.testId ??
    args?.parentId ??
    null
  const parentId = args?.parentId ?? result?.test?.id ?? args?.testId ?? null
  const hasToolError = isToolError(isComplete, result)
  const shouldInvalidate =
    isWriteAction && isComplete && !isError && !hasToolError

  useEffect(() => {
    if (!shouldInvalidate) return

    if (testId) {
      queryClient.invalidateQueries({
        queryKey: trpc.guidedTemplate.getStatusById.queryKey({ id: testId }),
      })
    }
    // Invalidate subtask queries for parent
    if (parentId) {
      queryClient.invalidateQueries({
        queryKey: trpc.subtask.list.queryKey({ parentId }),
      })
      queryClient.invalidateQueries({
        queryKey: trpc.subtask.getStats.queryKey({ parentId }),
      })
    }
    // Invalidate controls tree for status changes
    queryClient.invalidateQueries({
      queryKey: trpc.compliance.getControlsTree.queryKey(),
    })
  }, [shouldInvalidate, testId, parentId, queryClient])

  if (isComplete && (isError || isToolError(isComplete, result))) {
    return (
      <ToolErrorGuidance
        toolName='tests'
        errorMessage={getErrorMessage(result)}
      />
    )
  }

  // list: show summary with expandable list
  if (action === 'list' && isComplete && result) {
    const tests: any[] = Array.isArray(result.tests) ? result.tests : []
    const total = result.total ?? tests.length
    const filterParts: string[] = []
    if (args?.checkId) filterParts.push(`checkId: ${args.checkId}`)
    if (args?.controlRef) filterParts.push(`control: ${args.controlRef}`)
    if (args?.status) filterParts.push(`status: ${args.status}`)
    if (args?.category) filterParts.push(`category: ${args.category}`)
    if (args?.type) filterParts.push(`type: ${args.type}`)
    if (args?.userId) filterParts.push(`userId: ${args.userId}`)
    if (args?.dueBefore) filterParts.push(`dueBefore: ${args.dueBefore}`)
    if (args?.search) filterParts.push(`search: ${args.search}`)
    const filterSummary = filterParts.join(', ')

    return (
      <div className='text-muted-foreground py-1 text-xs'>
        <button
          type='button'
          onClick={() => tests.length > 0 && setExpanded((v) => !v)}
          className={`flex w-full items-center gap-2 ${tests.length > 0 ? 'cursor-pointer' : ''}`}
          disabled={tests.length === 0}
        >
          <ListTodo className='h-3.5 w-3.5 shrink-0 text-emerald-500' />
          <span className='flex-1 text-left'>
            {total} test{total !== 1 ? 's' : ''} found
            {tests.length < total ? ` (showing ${tests.length})` : ''}
            {filterSummary ? ` · ${filterSummary}` : ''}
          </span>
          {tests.length > 0 &&
            (expanded ? (
              <ChevronDown className='text-muted-foreground h-3.5 w-3.5 shrink-0' />
            ) : (
              <ChevronRight className='text-muted-foreground h-3.5 w-3.5 shrink-0' />
            ))}
        </button>
        {expanded && (
          <div className='mt-1.5 ml-5 space-y-1'>
            {tests.map((t: any, i: number) => (
              <div
                key={t.id ?? i}
                className='text-muted-foreground flex items-start gap-1.5 text-xs'
              >
                <ClipboardCheck className='mt-0.5 h-3 w-3 shrink-0 text-cobalt-strong' />
                <span>
                  {t.taskCode ? `#${t.taskCode} ` : ''}
                  {t.title ?? 'Untitled test'}
                  <span className='ml-1 text-[10px] text-text-placeholder'>
                    ({t.status ?? 'unknown'})
                  </span>
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }

  // get: show test detail summary
  if (action === 'get' && isComplete && result?.test) {
    const t = result.test
    const subtasks: any[] = Array.isArray(t.subtasks) ? t.subtasks : []
    return (
      <div className='text-muted-foreground py-1 text-xs'>
        <button
          type='button'
          onClick={() => setExpanded((v) => !v)}
          className='flex w-full cursor-pointer items-center gap-2'
        >
          <ClipboardCheck className='h-3.5 w-3.5 shrink-0 text-emerald-500' />
          <span className='flex-1 text-left'>
            {t.taskCode ? `#${t.taskCode} ` : ''}
            {t.title ?? 'Test details loaded'}
            <span className='ml-1 text-[10px] text-text-placeholder'>
              ({t.status ?? 'unknown'})
            </span>
            {subtasks.length > 0 && (
              <span className='ml-1 text-[10px] text-text-subtle'>
                · {subtasks.length} subtask{subtasks.length !== 1 ? 's' : ''}
              </span>
            )}
          </span>
          {expanded ? (
            <ChevronDown className='text-muted-foreground h-3.5 w-3.5 shrink-0' />
          ) : (
            <ChevronRight className='text-muted-foreground h-3.5 w-3.5 shrink-0' />
          )}
        </button>
        {expanded && (
          <div className='mt-1.5 ml-5 space-y-1'>
            {t.assignedTo && (
              <div className='text-[10px] text-text-subtle'>
                Assigned: {t.assignedTo.name ?? t.assignedTo.email}
              </div>
            )}
            {t.dueDate && (
              <div className='text-[10px] text-text-subtle'>
                Due: {new Date(t.dueDate).toLocaleDateString()}
              </div>
            )}
            {t.controlRefs?.length > 0 && (
              <div className='text-[10px] text-text-subtle'>
                Controls:{' '}
                {t.controlRefs
                  .map((c: any) =>
                    typeof c === 'string' ? c : (c.displayCode ?? c.id)
                  )
                  .join(', ')}
              </div>
            )}
            {subtasks.length > 0 && (
              <div className='mt-1 space-y-0.5'>
                {subtasks.map((s: any, i: number) => (
                  <div
                    key={s.id ?? i}
                    className='flex items-start gap-1.5 text-[10px] text-text-subtle'
                  >
                    <ListTodo className='mt-0.5 h-2.5 w-2.5 shrink-0' />
                    <span>
                      {s.title ?? 'Subtask'}{' '}
                      <span className='text-text-placeholder'>({s.status})</span>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    )
  }

  // Write actions: show success result
  if (isComplete && result) {
    const message = result.message
    const writeIcons: Record<string, React.ReactNode> = {
      assign: <UserCheck className='h-3.5 w-3.5 shrink-0 text-emerald-500' />,
      assignSubtask: (
        <UserCheck className='h-3.5 w-3.5 shrink-0 text-emerald-500' />
      ),
      bulkAssign: (
        <UserCheck className='h-3.5 w-3.5 shrink-0 text-emerald-500' />
      ),
      setDueDate: <Clock className='h-3.5 w-3.5 shrink-0 text-emerald-500' />,
      updateStatus: (
        <CheckCircle2 className='h-3.5 w-3.5 shrink-0 text-emerald-500' />
      ),
      complete: (
        <CheckCircle2 className='h-3.5 w-3.5 shrink-0 text-emerald-500' />
      ),
    }

    if (message) {
      return (
        <div className='text-muted-foreground flex items-center gap-2 py-1 text-xs'>
          {writeIcons[action] ?? (
            <CheckCircle2 className='h-3.5 w-3.5 shrink-0 text-emerald-500' />
          )}
          <span className='truncate'>{message}</span>
        </div>
      )
    }
  }

  // listSubtasks: show expandable list
  if (action === 'listSubtasks' && isComplete && result) {
    const subtasks: any[] = Array.isArray(result.subtasks)
      ? result.subtasks
      : []
    return (
      <div className='text-muted-foreground py-1 text-xs'>
        <button
          type='button'
          onClick={() => subtasks.length > 0 && setExpanded((v) => !v)}
          className={`flex w-full items-center gap-2 ${subtasks.length > 0 ? 'cursor-pointer' : ''}`}
          disabled={subtasks.length === 0}
        >
          <ListTodo className='h-3.5 w-3.5 shrink-0 text-emerald-500' />
          <span className='flex-1 text-left'>
            {subtasks.length} subtask{subtasks.length !== 1 ? 's' : ''}
          </span>
          {subtasks.length > 0 &&
            (expanded ? (
              <ChevronDown className='text-muted-foreground h-3.5 w-3.5 shrink-0' />
            ) : (
              <ChevronRight className='text-muted-foreground h-3.5 w-3.5 shrink-0' />
            ))}
        </button>
        {expanded && (
          <div className='mt-1.5 ml-5 space-y-1'>
            {subtasks.map((s: any, i: number) => (
              <div
                key={s.id ?? i}
                className='text-muted-foreground flex items-start gap-1.5 text-xs'
              >
                <ListTodo className='mt-0.5 h-3 w-3 shrink-0' />
                <span>
                  {s.title ?? 'Subtask'}
                  <span className='ml-1 text-[10px] text-text-placeholder'>
                    ({s.status ?? 'unknown'})
                  </span>
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }

  // Default: loading/done status line
  let detail: string | null = null
  if (action === 'assign' || action === 'assignSubtask') {
    detail = args?.email ?? null
  } else if (action === 'updateStatus' || action === 'complete') {
    detail = args?.status ?? 'APPROVED'
  }

  return (
    <ToolStatusLine
      isComplete={isComplete}
      isError={isError}
      icon={<Loader2 className='h-3.5 w-3.5 shrink-0 animate-spin' />}
      completedIcon={
        <ListTodo className='h-3.5 w-3.5 shrink-0 text-emerald-500' />
      }
      loadingLabel={labels.loading}
      completedLabel={labels.done}
      detail={detail}
    />
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// RUNBOOK TOOL — RFC-151: Executable test runbooks with action pills
// ═══════════════════════════════════════════════════════════════════════════════

const RUNBOOK_ACTION_LABELS: Record<string, { loading: string; done: string }> =
  {
    link: { loading: 'Linking to control...', done: 'Linked to control' },
    update: { loading: 'Updating procedure...', done: 'Procedure updated' },
  }

export function RunbookToolMessage({ part }: ToolPartProps) {
  const { isComplete, isError, result, args } = getToolState(part)
  const navigate = useNavigate()
  const action: string = args?.action ?? 'search'
  const labels = RUNBOOK_ACTION_LABELS[action] ?? {
    loading: 'Processing procedure...',
    done: 'Procedure processed',
  }

  if (isComplete && (isError || isToolError(isComplete, result))) {
    return (
      <ToolErrorGuidance
        toolName='runbook'
        errorMessage={getErrorMessage(result)}
      />
    )
  }

  // Link action — runbook linked to control, CES created
  if (action === 'link' && isComplete && result?.statusId) {
    const workspaceId =
      window.location.pathname.match(/workspaces\/([^/]+)/)?.[1] ?? ''

    return (
      <div className='my-2 w-full max-w-lg rounded-md border border-status-green-border-subtle bg-status-green-bg px-4 py-3'>
        <div className='flex items-center gap-2'>
          <ClipboardCheck className='h-4 w-4 shrink-0 text-status-green-strong' />
          <span className='text-[12px] font-medium text-status-green-strong'>
            Linked to {result.displayCode ?? result.controlId}
          </span>
        </div>
        <p className='mt-1 text-[11px] text-status-green-strong'>
          {result.controlTitle}
        </p>
        <button
          className='mt-2 flex items-center gap-1.5 rounded-md border border-status-green-border-subtle bg-card px-3 py-1.5 text-[11px] font-medium text-status-green-strong hover:bg-status-green-bg'
          onClick={() => {
            void navigate({
              to: '/workspaces/$workspaceId/tests/$testId',
              params: { workspaceId, testId: result.statusId },
            })
          }}
        >
          <ArrowRight className='h-3 w-3' />
          View Test
        </button>
      </div>
    )
  }

  let detail: string | null = null
  if (isComplete && result) {
    if (action === 'update') {
      detail = result.message || null
    }
  }

  return (
    <ToolStatusLine
      isComplete={isComplete}
      isError={isError}
      icon={<Loader2 className='h-3.5 w-3.5 shrink-0 animate-spin' />}
      completedIcon={
        <ClipboardCheck className='h-3.5 w-3.5 shrink-0 text-emerald-500' />
      }
      loadingLabel={labels.loading}
      completedLabel={labels.done}
      detail={detail}
    />
  )
}

const RUNBOOK_EXECUTION_ACTION_LABELS: Record<
  string,
  { loading: string; done: string }
> = {
  execute: { loading: 'Starting procedure...', done: 'Procedure started' },
  status: {
    loading: 'Loading procedure status...',
    done: 'Procedure status loaded',
  },
  resume: { loading: 'Resuming procedure...', done: 'Procedure resumed' },
  approve: {
    loading: 'Approving procedure step...',
    done: 'Procedure step approved',
  },
  reject: {
    loading: 'Rejecting procedure step...',
    done: 'Procedure step rejected',
  },
}

function formatRunbookExecutionStatus(status: string | null | undefined) {
  switch (status) {
    case 'running':
      return 'Procedure running'
    case 'completed':
      return 'Procedure completed'
    case 'partial':
      return 'Procedure completed with failures'
    case 'failed':
      return 'Procedure failed'
    case 'paused':
      return 'Procedure paused'
    case 'awaiting_human':
      return 'Procedure waiting for approval'
    default:
      return 'Procedure status loaded'
  }
}

export function RunbookExecutionToolMessage({ part }: ToolPartProps) {
  const { isComplete, isError, result, args } = getToolState(part)
  const action: string = result?.action ?? args?.action ?? 'execute'
  const labels = RUNBOOK_EXECUTION_ACTION_LABELS[action] ?? {
    loading: 'Processing procedure execution...',
    done: 'Procedure execution updated',
  }

  if (isComplete && (isError || isToolError(isComplete, result))) {
    return (
      <ToolErrorGuidance
        toolName='runbookExecution'
        errorMessage={getErrorMessage(result)}
      />
    )
  }

  if (isComplete && result?.needsInputs) {
    const missingInputs = Array.isArray(result.missingInputs)
      ? result.missingInputs
      : []
    const invalidInputs = Array.isArray(result.invalidInputs)
      ? result.invalidInputs
      : []
    return (
      <div className='my-2 w-full max-w-2xl rounded-md border border-border bg-card p-4'>
        <div className='flex items-start gap-3'>
          <ListTodo className='mt-0.5 h-4 w-4 shrink-0 text-cobalt-strong' />
          <div className='min-w-0 flex-1'>
            <div className='font-heading text-[13px] font-semibold text-text-secondary'>
              Procedure needs inputs
            </div>
            <p className='mt-1 text-[12px] leading-relaxed text-text-body'>
              Vera needs these values before starting
              {result.templateTitle
                ? ` "${result.templateTitle}"`
                : ' the procedure'}
              .
            </p>
            {missingInputs.length > 0 && (
              <div className='mt-3 space-y-2'>
                {missingInputs.map((input: any) => (
                  <div
                    key={input.name}
                    className='rounded-md border border-border bg-background px-3 py-2'
                  >
                    <div className='flex items-center gap-2'>
                      <span className='font-mono text-[12px] font-medium text-text-secondary'>
                        {input.name}
                      </span>
                      <span className='rounded-full bg-card px-2 py-0.5 font-mono text-[10px] text-text-subtle'>
                        {input.type}
                      </span>
                    </div>
                    {input.description && (
                      <p className='mt-1 text-[11px] leading-relaxed text-text-subtle'>
                        {input.description}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
            {invalidInputs.length > 0 && (
              <p className='mt-3 text-[12px] text-status-orange-strong'>
                Some provided values were invalid. Vera will ask again with the
                expected format.
              </p>
            )}
          </div>
        </div>
      </div>
    )
  }

  const executionId =
    result?.executionId ?? result?.id ?? args?.executionId ?? null

  if (
    isComplete &&
    executionId &&
    (action === 'execute' ||
      action === 'resume' ||
      action === 'approve' ||
      action === 'reject')
  ) {
    return (
      <div className='my-2 w-full max-w-2xl overflow-hidden rounded-md border border-border bg-card'>
        <RunbookRunContent
          runId={executionId}
          layout='embedded'
          allowHumanReviewActions
        />
      </div>
    )
  }

  let detail: string | null = null
  if (action === 'status') {
    const counts =
      typeof result?.successfulSteps === 'number' &&
      typeof result?.totalSteps === 'number'
        ? `${result.successfulSteps}/${result.totalSteps} passed`
        : null
    const finalStep =
      result?.finalStepResult &&
      typeof result.finalStepResult.stepIndex === 'number'
        ? `step ${result.finalStepResult.stepIndex}`
        : null
    detail = [counts, finalStep].filter(Boolean).join(' · ') || null
  } else if (args?.templateTitle || result?.templateTitle) {
    detail = args?.templateTitle ?? result?.templateTitle ?? null
  }

  const completedLabel =
    action === 'status'
      ? formatRunbookExecutionStatus(result?.status)
      : labels.done

  return (
    <ToolStatusLine
      isComplete={isComplete}
      isError={isError}
      icon={<Loader2 className='h-3.5 w-3.5 shrink-0 animate-spin' />}
      completedIcon={
        <ClipboardCheck className='h-3.5 w-3.5 shrink-0 text-emerald-500' />
      }
      loadingLabel={labels.loading}
      completedLabel={completedLabel}
      detail={detail}
    />
  )
}
